import React, { useState, useEffect, useMemo, startTransition, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import AccountSwitcher from '../../../components/AccountSwitcher';
import { useAccounts } from '../../../context/AccountContext';
import { Card } from '../../../components/ui/Card';
import { LineChart, BarChart, DoughnutChart } from '../../../components/charts';
import {
  Loader2,
  DollarSign,
  TrendingUp,
  MousePointerClick,
  Target,
  Share2,
  ShoppingCart,
  Eye,
  ThumbsUp,
  BarChart3,
  RefreshCw,
  Download,
  Users,
  Smartphone,
  MessageSquare,
  Layers,
  Globe,
  Filter,
  Search,
  Calendar,
  ChevronRight,
  ChevronDown,
  ArrowUpRight,
  Check,
  Edit2,
  X,
  AlertTriangle,
  History,
  Clock,
  XCircle,
  Activity
} from 'lucide-react';
import axios from 'axios';
import LoadingScreen from '../../../components/LoadingScreen';
import Ga4WebOnlyNote from '../../../components/Ga4WebOnlyNote';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

// Function to format objective names to be more readable
const formatObjectiveName = (objective) => {
  if (!objective) return 'Unknown';
  
  // Handle prefixed objectives like 'OUTCOME_SALES'
  if (objective.includes('OUTCOME_')) {
    return objective.replace('OUTCOME_', '').split('_').map(
      word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  }
  
  // Handle objectives like 'APP_INSTALLS'
  if (objective.includes('_')) {
    return objective.split('_').map(
      word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  }
  
  // Handle camelCase objectives
  if (/[a-z][A-Z]/.test(objective)) {
    return objective
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
  
  // Default case - just capitalize the first letter
  return objective.charAt(0).toUpperCase() + objective.slice(1).toLowerCase();
};

export default function TikTokCampaignType() {
  const { selectedBrand } = useAuth();
  const { getSelectedAccountId } = useAccounts();
  const selectedAccountId = getSelectedAccountId('TIKTOK_ADS');
  const [loading, setLoading] = useState(true);
  const [selectedDateRange, setSelectedDateRange] = useState('7');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [selectedObjective, setSelectedObjective] = useState('all');
  const [metaAccountFilter, setMetaAccountFilter] = useState('all');
  const [metaData, setMetaData] = useState(null);
  // All-time dataset for monthly pivot (unaffected by top filter)
  const [metaPivotData, setMetaPivotData] = useState(null);
  const [expandedCampaigns, setExpandedCampaigns] = useState({});
  const [expandedAdSets, setExpandedAdSets] = useState({});
  const DEMO_BRAND_IDS_TIKTOK = useMemo(() => new Set(['2b08baa8-dd8e-4b5c-93cd-dd38b9b3718a', 'ec9873c2-0edb-44eb-ac6f-2670c8e56dc4']), []);

  // Live status/budget overlay — keyed by String(campaign.id)
  const [liveDataMap, setLiveDataMap] = useState({});
  const [isLoadingLive, setIsLoadingLive] = useState(false);
  const [openStatusDropdown, setOpenStatusDropdown] = useState(null);
  const [editBudget, setEditBudget] = useState(null); // { id, value }
  const [updatingId, setUpdatingId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, message, onConfirm, variant }

  // Per-objective campaign name filter (tag-based)
  const [objectiveFilterTags, setObjectiveFilterTags] = useState({});
  const [objectiveFilterInputs, setObjectiveFilterInputs] = useState({});

  // Change History state
  const [showChangeHistory, setShowChangeHistory] = useState(false);
  const [changeHistoryData, setChangeHistoryData] = useState([]);
  const [changeHistoryTotal, setChangeHistoryTotal] = useState(0);
  const [changeHistoryLoading, setChangeHistoryLoading] = useState(false);
  const [changeHistoryFilter, setChangeHistoryFilter] = useState('all');

  // Budget Utilization state
  const [targetBudget, setTargetBudget] = useState(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem('tiktokTargetBudget') : null;
    return saved ? parseInt(saved, 10) : 100000;
  });
  const [isEditingBudget, setIsEditingBudget] = useState(false);
  useEffect(() => {
    try { localStorage.setItem('tiktokTargetBudget', String(targetBudget)); } catch (_) {}
  }, [targetBudget]);

  // Daily trend from campaign rows (spend + revenue by date, sorted ASC)
  const displayDailyData = useMemo(() => {
    if (!Array.isArray(metaData) || metaData.length === 0) return null;
    const byDate = new Map();
    for (const r of metaData) {
      const d = (r.date || '').split('T')[0];
      if (!d) continue;
      if (!byDate.has(d)) byDate.set(d, { spend: 0, revenue: 0 });
      const agg = byDate.get(d);
      agg.spend += Number(r.spend || 0);
      // Prefer ga4 revenue; fall back to TikTok conversion_value
      agg.revenue += Number(r.ga4TotalRevenue || 0) || Number(r.purchaseValue || 0);
    }
    const sortedDates = Array.from(byDate.keys()).sort();
    return {
      labels: sortedDates.map(d => {
        const dt = new Date(d);
        return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
      }),
      spend: sortedDates.map(d => byDate.get(d).spend),
      revenue: sortedDates.map(d => byDate.get(d).revenue)
    };
  }, [metaData]);

  // Account filter options from data
  const accountNameMap = useMemo(() => {
    const map = {};
    if (metaData && Array.isArray(metaData)) {
      metaData.forEach(r => { if (r.accountId && r.accountName) map[r.accountId] = r.accountName; });
    }
    return map;
  }, [metaData]);

  const metaAccountOptions = useMemo(() => {
    const ids = new Set();
    if (metaData && Array.isArray(metaData)) {
      metaData.forEach(r => { if (r.accountId) ids.add(r.accountId); });
    }
    return Array.from(ids).sort();
  }, [metaData]);

  const filteredMetaData = useMemo(() => {
    if (!metaData || !Array.isArray(metaData) || metaAccountFilter === 'all') return metaData;
    return metaData.filter(r => r.accountId === metaAccountFilter);
  }, [metaData, metaAccountFilter]);

  // Computed metrics from API data
  const computedMetrics = useMemo(() => {
    if (!filteredMetaData || !Array.isArray(filteredMetaData) || filteredMetaData.length === 0) {
      return {
        totalSpend: 0,
        totalImpressions: 0,
        totalClicks: 0,
        totalPurchaseConversions: 0,
        totalPurchaseValue: 0,
        totalGa4Sessions: 0,
        totalGa4Purchases: 0,
        totalGa4Users: 0,
        totalGa4NewUsers: 0,
        totalGa4Revenue: 0,
        totalAdjustRevenue: 0,
        totalAdjustSessions: 0,
        totalAdjustPurchases: 0,
        totalAdjustInstalls: 0,
        averageCtr: 0,
        averageCpc: 0,
        roas: 0,
        cpa: 0,
        campaignObjectives: []
      };
    }

    // Calculate totals from filteredMetaData
    const totals = filteredMetaData.reduce((acc, item) => {
      return {
        totalSpend: acc.totalSpend + Number(item.spend || 0),
        totalImpressions: acc.totalImpressions + Number(item.impressions || 0),
        totalClicks: acc.totalClicks + Number(item.clicks || 0),
        totalReach: acc.totalReach + Number(item.reach || 0),
        totalView: acc.totalView + Number(item.view || 0),
        totalTrueplay: acc.totalTrueplay + Number(item.trueplay || 0),
        totalPurchaseConversions: acc.totalPurchaseConversions + Number(item.purchaseConversions || 0),
        totalPurchaseValue: acc.totalPurchaseValue + Number(item.purchaseValue || 0),
        totalGa4Sessions: acc.totalGa4Sessions + Number(item.ga4Sessions || 0),
        totalGa4Purchases: acc.totalGa4Purchases + Number(item.ga4Purchases || 0),
        totalGa4Users: acc.totalGa4Users + Number(item.ga4Users || 0),
        totalGa4NewUsers: acc.totalGa4NewUsers + Number(item.ga4NewUsers || 0),
        totalGa4Revenue: acc.totalGa4Revenue + Number(item.ga4TotalRevenue || 0),
        totalAdjustRevenue: acc.totalAdjustRevenue + Number(item.adjustAllRevenue || 0),
        totalAdjustSessions: acc.totalAdjustSessions + Number(item.adjustSessions || 0),
        totalAdjustPurchases: acc.totalAdjustPurchases + Number(item.adjustPurchases || 0),
        totalAdjustInstalls: acc.totalAdjustInstalls + Number(item.adjustInstalls || 0)
      };
    }, {
      totalSpend: 0,
      totalImpressions: 0,
      totalClicks: 0,
      totalReach: 0,
      totalView: 0,
      totalTrueplay: 0,
      totalPurchaseConversions: 0,
      totalPurchaseValue: 0,
      totalGa4Sessions: 0,
      totalGa4Purchases: 0,
      totalGa4Users: 0,
      totalGa4NewUsers: 0,
      totalGa4Revenue: 0,
      totalAdjustRevenue: 0,
      totalAdjustSessions: 0,
      totalAdjustPurchases: 0,
      totalAdjustInstalls: 0
    });

    // Group by campaign objective
    const objectiveGroups = {};
    const campaignsByObjective = {};
    
    filteredMetaData.forEach(item => {
      const objective = item.campaignObjective || 'Unknown';
      const campaignId = item.campaignId;
      const campaignName = item.campaignName;
      
      // Skip if no campaign ID/name
      if (!campaignId || !campaignName) return;
      
      // Initialize objective group if not exists
      if (!objectiveGroups[objective]) {
        objectiveGroups[objective] = {
          id: objective.toLowerCase().replace(/\s+/g, '_'),
          name: formatObjectiveName(objective),
          spend: 0,
          impressions: 0,
          clicks: 0,
          reach: 0,
          view: 0,
          trueplay: 0,
          purchaseConversions: 0,
          purchaseValue: 0,
          ga4Sessions: 0,
          ga4Purchases: 0,
          ga4Users: 0,
          ga4NewUsers: 0,
          ga4Revenue: 0,
          totalBudget: 0,
          campaigns: new Set() // Track unique campaigns
        };
        
        campaignsByObjective[objective] = {};
      }
      
      // Initialize campaign if not exists
      if (!campaignsByObjective[objective][campaignId]) {
        campaignsByObjective[objective][campaignId] = {
          id: campaignId,
          name: campaignName,
          status: item.campaignStatus || 'active',
          budgetAmount: Number(item.budgetAmount || 0),
          budgetDate: item.date, // Store the date for this budget record
          spend: 0,
          impressions: 0,
          clicks: 0,
          reach: 0,
          view: 0,
          trueplay: 0,
          purchaseConversions: 0,
          purchaseValue: 0,
          ga4Sessions: 0,
          ga4Purchases: 0,
          ga4Users: 0,
          ga4NewUsers: 0,
          ga4Revenue: 0,
          adjustRevenue: 0,
          adjustSessions: 0,
          adjustPurchases: 0,
          adjustInstalls: 0
        };
      } else {
        // Update budget if this record has a more recent date
        const existingBudgetDate = new Date(campaignsByObjective[objective][campaignId].budgetDate);
        const currentBudgetDate = new Date(item.date);
        
        if (currentBudgetDate > existingBudgetDate) {
          campaignsByObjective[objective][campaignId].budgetAmount = Number(item.budgetAmount || 0);
          campaignsByObjective[objective][campaignId].budgetDate = item.date;
        }
      }
      
      // Add to objective totals
      objectiveGroups[objective].spend += Number(item.spend || 0);
      objectiveGroups[objective].impressions += Number(item.impressions || 0);
      objectiveGroups[objective].clicks += Number(item.clicks || 0);
      objectiveGroups[objective].reach += Number(item.reach || 0);
      objectiveGroups[objective].view += Number(item.view || 0);
      objectiveGroups[objective].trueplay += Number(item.trueplay || 0);
      objectiveGroups[objective].purchaseConversions += Number(item.purchaseConversions || 0);
      objectiveGroups[objective].purchaseValue += Number(item.purchaseValue || 0);
      objectiveGroups[objective].ga4Sessions += Number(item.ga4Sessions || 0);
      objectiveGroups[objective].ga4Purchases += Number(item.ga4Purchases || 0);
      objectiveGroups[objective].ga4Users += Number(item.ga4Users || 0);
      objectiveGroups[objective].ga4NewUsers += Number(item.ga4NewUsers || 0);
      objectiveGroups[objective].ga4Revenue += Number(item.ga4TotalRevenue || 0);
      objectiveGroups[objective].campaigns.add(campaignId);
      // Adjust metrics
      if (!('adjustRevenue' in objectiveGroups[objective])) {
        objectiveGroups[objective].adjustRevenue = 0;
        objectiveGroups[objective].adjustSessions = 0;
        objectiveGroups[objective].adjustPurchases = 0;
        objectiveGroups[objective].adjustInstalls = 0;
      }
      objectiveGroups[objective].adjustRevenue += Number(item.adjustAllRevenue || 0);
      objectiveGroups[objective].adjustSessions += Number(item.adjustSessions || 0);
      objectiveGroups[objective].adjustPurchases += Number(item.adjustPurchases || 0);
      objectiveGroups[objective].adjustInstalls += Number(item.adjustInstalls || 0);
      
      // Add to campaign totals
      campaignsByObjective[objective][campaignId].spend += Number(item.spend || 0);
      campaignsByObjective[objective][campaignId].impressions += Number(item.impressions || 0);
      campaignsByObjective[objective][campaignId].clicks += Number(item.clicks || 0);
      campaignsByObjective[objective][campaignId].reach += Number(item.reach || 0);
      campaignsByObjective[objective][campaignId].view += Number(item.view || 0);
      campaignsByObjective[objective][campaignId].trueplay += Number(item.trueplay || 0);
      campaignsByObjective[objective][campaignId].purchaseConversions += Number(item.purchaseConversions || 0);
      campaignsByObjective[objective][campaignId].purchaseValue += Number(item.purchaseValue || 0);
      campaignsByObjective[objective][campaignId].ga4Sessions += Number(item.ga4Sessions || 0);
      campaignsByObjective[objective][campaignId].ga4Purchases += Number(item.ga4Purchases || 0);
      campaignsByObjective[objective][campaignId].ga4Users += Number(item.ga4Users || 0);
      campaignsByObjective[objective][campaignId].ga4NewUsers += Number(item.ga4NewUsers || 0);
      campaignsByObjective[objective][campaignId].ga4Revenue += Number(item.ga4TotalRevenue || 0);
      // Adjust metrics per campaign
      campaignsByObjective[objective][campaignId].adjustRevenue += Number(item.adjustAllRevenue || 0);
      campaignsByObjective[objective][campaignId].adjustSessions += Number(item.adjustSessions || 0);
      campaignsByObjective[objective][campaignId].adjustPurchases += Number(item.adjustPurchases || 0);
      campaignsByObjective[objective][campaignId].adjustInstalls += Number(item.adjustInstalls || 0);
      
      // Track ad sets for each campaign
      if (!campaignsByObjective[objective][campaignId].adSets) {
        campaignsByObjective[objective][campaignId].adSets = {};
      }
      
      // Get adset info if available
      const adsetId = item.adsetId;
      const adsetName = item.adsetName;
      
      if (adsetId && adsetName) {
        if (!campaignsByObjective[objective][campaignId].adSets[adsetId]) {
          campaignsByObjective[objective][campaignId].adSets[adsetId] = {
            id: adsetId,
            name: adsetName,
            spend: 0,
            impressions: 0,
            clicks: 0,
            purchaseConversions: 0,
            purchaseValue: 0,
            ads: {} // New: Track ads for each ad set
          };
        }
        
        // Add to adset totals
        campaignsByObjective[objective][campaignId].adSets[adsetId].spend += Number(item.spend || 0);
        campaignsByObjective[objective][campaignId].adSets[adsetId].impressions += Number(item.impressions || 0);
        campaignsByObjective[objective][campaignId].adSets[adsetId].clicks += Number(item.clicks || 0);
        campaignsByObjective[objective][campaignId].adSets[adsetId].purchaseConversions += Number(item.purchaseConversions || 0);
        campaignsByObjective[objective][campaignId].adSets[adsetId].purchaseValue += Number(item.purchaseValue || 0);
        
        // Track ads for each ad set
        const adId = item.adId;
        const adName = item.adName;
        
        if (adId && adName) {
          if (!campaignsByObjective[objective][campaignId].adSets[adsetId].ads[adId]) {
            campaignsByObjective[objective][campaignId].adSets[adsetId].ads[adId] = {
              id: adId,
              name: adName,
              spend: 0,
              impressions: 0,
              clicks: 0,
              purchaseConversions: 0,
              purchaseValue: 0
            };
          }
          
          // Add to ad totals
          campaignsByObjective[objective][campaignId].adSets[adsetId].ads[adId].spend += Number(item.spend || 0);
          campaignsByObjective[objective][campaignId].adSets[adsetId].ads[adId].impressions += Number(item.impressions || 0);
          campaignsByObjective[objective][campaignId].adSets[adsetId].ads[adId].clicks += Number(item.clicks || 0);
          campaignsByObjective[objective][campaignId].adSets[adsetId].ads[adId].purchaseConversions += Number(item.purchaseConversions || 0);
          campaignsByObjective[objective][campaignId].adSets[adsetId].ads[adId].purchaseValue += Number(item.purchaseValue || 0);
        }
      }
    });
    
    // Convert to array and calculate derived metrics
    const campaignObjectives = [];
    for (const objective in objectiveGroups) {
      const obj = objectiveGroups[objective];
      
      // Calculate metrics
      const share = totals.totalSpend > 0 ? (obj.spend / totals.totalSpend * 100) : 0;
      const ctr = obj.impressions > 0 ? (obj.clicks / obj.impressions * 100) : 0;
      const cpc = obj.clicks > 0 ? (obj.spend / obj.clicks) : 0;
      const cpa = obj.purchaseConversions > 0 ? (obj.spend / obj.purchaseConversions) : 0;
      // ROAS calculated from GA4 Revenue + Adjust Revenue
      const roas = obj.spend > 0 ? ((Number(obj.ga4Revenue || 0) + Number(obj.adjustRevenue || 0)) / obj.spend) : 0;
      
      // Convert Set to array for campaign count
      const campaignCount = obj.campaigns.size;
      
      // Get campaign list and calculate their metrics
      const campaignList = [];
      let totalBudgetForObjective = 0;
      
      if (campaignsByObjective[objective]) {
        for (const campId in campaignsByObjective[objective]) {
          const campaign = campaignsByObjective[objective][campId];
          
          // Add to total budget for this objective (once per campaign)
          totalBudgetForObjective += campaign.budgetAmount;
          
          const campCtr = campaign.impressions > 0 ? (campaign.clicks / campaign.impressions * 100) : 0;
          const campCpc = campaign.clicks > 0 ? (campaign.spend / campaign.clicks) : 0;
          const campRoas = campaign.spend > 0 ? ((Number(campaign.ga4Revenue || 0) + Number(campaign.adjustRevenue || 0)) / campaign.spend) : 0;
          
          // Process adSets for this campaign
          const adSets = [];
          
          if (campaign.adSets) {
            for (const adSetId in campaign.adSets) {
              const adSet = campaign.adSets[adSetId];
              
              const adSetCtr = adSet.impressions > 0 ? (adSet.clicks / adSet.impressions * 100) : 0;
              const adSetCpc = adSet.clicks > 0 ? (adSet.spend / adSet.clicks) : 0;
              const adSetRoas = adSet.spend > 0 ? (adSet.purchaseValue / adSet.spend) : 0;
              
              // Process ads for this ad set
              const ads = [];
              
              if (adSet.ads) {
                for (const adId in adSet.ads) {
                  const ad = adSet.ads[adId];
                  
                  const adCtr = ad.impressions > 0 ? (ad.clicks / ad.impressions * 100) : 0;
                  const adCpc = ad.clicks > 0 ? (ad.spend / ad.clicks) : 0;
                  const adRoas = ad.spend > 0 ? (ad.purchaseValue / ad.spend) : 0;
                  
                  ads.push({
                    ...ad,
                    ctr: parseFloat(adCtr.toFixed(2)),
                    cpc: adCpc,
                    roas: adRoas
                  });
                }
              }
              
              // Sort ads by spend
              ads.sort((a, b) => b.spend - a.spend);
              
              adSets.push({
                ...adSet,
                ctr: parseFloat(adSetCtr.toFixed(2)),
                cpc: adSetCpc,
                roas: adSetRoas,
                ads: ads // Add processed ads to ad set
              });
            }
          }
          
          // Sort adSets by spend
          adSets.sort((a, b) => b.spend - a.spend);
          
          campaignList.push({
            ...campaign,
            ctr: parseFloat(campCtr.toFixed(2)),
            cpc: campCpc,
            roas: campRoas,
            budgetAmount: campaign.budgetAmount, // Add budget amount
            adSets: adSets // Add processed adSets to campaign
          });
        }
      }
      
      // Sort campaigns by spend
      campaignList.sort((a, b) => b.spend - a.spend);
      
      campaignObjectives.push({
        ...obj,
        share: parseFloat(share.toFixed(1)),
        ctr: parseFloat(ctr.toFixed(2)),
        cpc: cpc,
        cpa: cpa,
        roas: roas,
        revenue: obj.purchaseValue,
        conversions: obj.purchaseConversions,
        campaigns: campaignCount,
        totalBudget: totalBudgetForObjective,
        campaignList: campaignList // Show all campaigns
      });
    }

    // Sort objectives by spend
    campaignObjectives.sort((a, b) => b.spend - a.spend);

    // Calculate derived metrics
    const averageCtr = totals.totalImpressions > 0 
      ? (totals.totalClicks / totals.totalImpressions * 100)
      : 0;
      
    const averageCpc = totals.totalClicks > 0 
      ? (totals.totalSpend / totals.totalClicks)
      : 0;
      
    const roas = totals.totalSpend > 0 
      ? ((Number(totals.totalGa4Revenue || 0) + Number(totals.totalAdjustRevenue || 0)) / totals.totalSpend)
      : 0;
      
    // Calculate CPA (Cost Per Acquisition)
    const cpa = totals.totalPurchaseConversions > 0
      ? (totals.totalSpend / totals.totalPurchaseConversions)
      : 0;

    return {
      ...totals,
      averageCtr,
      averageCpc,
      roas,
      cpa,
      campaignObjectives
    };
  }, [filteredMetaData]);

  // Column visibility for Adjust metrics (hide when totals are zero)
  const showAdjRevCol = (computedMetrics?.totalAdjustRevenue || 0) > 0;
  const showAdjPurchCol = (computedMetrics?.totalAdjustPurchases || 0) > 0;
  const showAdjSessCol = (computedMetrics?.totalAdjustSessions || 0) > 0;
  const showAdjInstCol = (computedMetrics?.totalAdjustInstalls || 0) > 0;
  const hasAnyAdjustKpi = showAdjRevCol || showAdjPurchCol || showAdjSessCol || showAdjInstCol;

  // Trend metric selection (default Cost)
  const [selectedTrendMetric, setSelectedTrendMetric] = useState('cost');
  const [expandedObjectivesPivot, setExpandedObjectivesPivot] = useState({});

  // Monthly performance pivot: columns = months, rows = objectives and expandable campaigns
  const monthlyPivot = useMemo(() => {
    if (!Array.isArray(metaPivotData) || metaPivotData.length === 0) return { months: [], objectives: [] };

    const toMonthKey = (d) => {
      if (!d) return null;
      const dt = new Date(d);
      if (isNaN(dt)) return null;
      return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
    };

    // Collect months
    const monthSet = new Set();
    metaPivotData.forEach((row) => {
      const mk = toMonthKey(row.date || row.dateStart || row.dateStop);
      if (mk) monthSet.add(mk);
    });
    const months = Array.from(monthSet)
      .sort((a, b) => new Date(b + '-01') - new Date(a + '-01'))
      .slice(0, 6) // last 6 months
      .reverse(); // display oldest -> newest

    // Helper to ensure bucket obj
    const initBucket = () => ({
      spend: 0, clicks: 0, impressions: 0, conversions: 0,
      reach: 0, view: 0, trueplay: 0,
      revenue: 0, ga4Revenue: 0, ga4Sessions: 0, ga4Purchases: 0,
      adjustRevenue: 0, adjustSessions: 0, adjustPurchases: 0, adjustInstalls: 0
    });

    // Build objective -> campaign -> month buckets
    const objMap = new Map();
    metaPivotData.forEach((r) => {
      const objective = r.campaignObjective || 'Unknown';
      const campaignId = r.campaignId;
      const campaignName = r.campaignName || 'Unknown Campaign';
      const mk = toMonthKey(r.date || r.dateStart || r.dateStop);
      if (!mk || !months.includes(mk)) return;

      if (!objMap.has(objective)) {
        objMap.set(objective, { name: formatObjectiveName(objective), totals: new Map(), campaigns: new Map() });
      }
      const o = objMap.get(objective);
      if (!o.totals.has(mk)) o.totals.set(mk, initBucket());
      if (campaignId) {
        if (!o.campaigns.has(campaignId)) o.campaigns.set(campaignId, { id: campaignId, name: campaignName, totals: new Map() });
        const c = o.campaigns.get(campaignId);
        if (!c.totals.has(mk)) c.totals.set(mk, initBucket());
        const cb = c.totals.get(mk);
        cb.spend += Number(r.spend || 0);
        cb.clicks += Number(r.clicks || 0);
        cb.impressions += Number(r.impressions || 0);
        cb.reach += Number(r.reach || 0);
        cb.view += Number(r.view || 0);
        cb.trueplay += Number(r.trueplay || 0);
        cb.conversions += Number(r.purchaseConversions || r.conversions || 0);
        cb.revenue += Number(r.purchaseValue || r.conversion_value || 0);
        cb.ga4Revenue += Number(r.ga4TotalRevenue || 0);
        cb.ga4Sessions += Number(r.ga4Sessions || 0);
        cb.ga4Purchases += Number(r.ga4Purchases || 0);
        cb.adjustRevenue += Number(r.adjustAllRevenue || 0);
        cb.adjustSessions += Number(r.adjustSessions || 0);
        cb.adjustPurchases += Number(r.adjustPurchases || 0);
        cb.adjustInstalls += Number(r.adjustInstalls || 0);
      }
      const ob = o.totals.get(mk);
      ob.spend += Number(r.spend || 0);
      ob.clicks += Number(r.clicks || 0);
      ob.impressions += Number(r.impressions || 0);
      ob.reach += Number(r.reach || 0);
      ob.view += Number(r.view || 0);
      ob.trueplay += Number(r.trueplay || 0);
      ob.conversions += Number(r.purchaseConversions || r.conversions || 0);
      ob.revenue += Number(r.purchaseValue || r.conversion_value || 0);
      ob.ga4Revenue += Number(r.ga4TotalRevenue || 0);
      ob.ga4Sessions += Number(r.ga4Sessions || 0);
      ob.ga4Purchases += Number(r.ga4Purchases || 0);
      ob.adjustRevenue += Number(r.adjustAllRevenue || 0);
      ob.adjustSessions += Number(r.adjustSessions || 0);
      ob.adjustPurchases += Number(r.adjustPurchases || 0);
      ob.adjustInstalls += Number(r.adjustInstalls || 0);
    });

    // Convert to arrays
    const objectives = Array.from(objMap.entries()).map(([key, val]) => ({
      id: key,
      name: val.name,
      totals: months.map((m) => ({ monthKey: m, ...(val.totals.get(m) || initBucket()) })),
      campaigns: Array.from(val.campaigns.values()).map((c) => ({
        id: c.id,
        name: c.name,
        totals: months.map((m) => ({ monthKey: m, ...(c.totals.get(m) || initBucket()) })),
      })),
    })).sort((a, b) => {
      // sort by latest month spend desc
      const aSpend = a.totals[a.totals.length - 1]?.spend || 0;
      const bSpend = b.totals[b.totals.length - 1]?.spend || 0;
      return bSpend - aSpend;
    });

    return { months, objectives };
  }, [metaPivotData]);

  const formatPivotCell = (metric, b) => {
    switch (metric) {
      case 'cost':
        return formatCurrency(b.spend);
      case 'revenue':
        return formatCurrency(b.revenue);
      case 'adjustRevenue':
        return formatCurrency(b.adjustRevenue || 0);
      case 'costRevenue':
        return `${formatCurrency(b.spend)} / ${formatCurrency(b.revenue)}`;
      case 'impressions':
        return formatNumber(b.impressions);
      case 'reach':
        return formatNumber(b.reach || 0);
      case 'view':
        return formatNumber(b.view || 0);
      case 'trueplay':
        return formatNumber(b.trueplay || 0);
      case 'ga4Revenue':
        return formatCurrency(b.ga4Revenue);
      case 'adjustPurchases':
        return formatNumber(b.adjustPurchases || 0);
      case 'adjustSessions':
        return formatNumber(b.adjustSessions || 0);
      case 'adjustInstalls':
        return formatNumber(b.adjustInstalls || 0);
      case 'clicks':
        return formatNumber(b.clicks);
      case 'conversions':
        return formatNumber(b.conversions);
      case 'cpa':
        return formatCurrencyDecimals(b.conversions > 0 ? b.spend / b.conversions : 0);
      case 'ga4Purchases':
        return formatNumber(b.ga4Purchases);
      case 'sessions':
        return formatNumber(b.ga4Sessions);
      case 'roas':
        return `${(b.spend > 0 ? b.revenue / b.spend : 0).toFixed(2)}x`;
      case 'cpc':
        return formatCurrencyDecimals(b.clicks > 0 ? b.spend / b.clicks : 0);
      case 'cps':
        return formatCurrencyDecimals(b.ga4Sessions > 0 ? b.spend / b.ga4Sessions : 0);
      case 'cr':
        return `${(b.clicks > 0 ? (b.conversions / b.clicks) * 100 : 0).toFixed(2)}%`;
      default:
        return formatCurrency(b.spend);
    }
  };

  // Aggregate helpers for totals/rows
  const aggregateBuckets = (buckets) => {
    const agg = {
      spend: 0,
      clicks: 0,
      impressions: 0,
      reach: 0,
      view: 0,
      trueplay: 0,
      conversions: 0,
      revenue: 0,
      adjustRevenue: 0,
      ga4Revenue: 0,
      ga4Sessions: 0,
      ga4Purchases: 0,
      adjustSessions: 0,
      adjustPurchases: 0,
      adjustInstalls: 0,
    };
    (buckets || []).forEach((b) => {
      if (!b) return;
      agg.spend += Number(b.spend || 0);
      agg.clicks += Number(b.clicks || 0);
      agg.impressions += Number(b.impressions || 0);
      agg.reach += Number(b.reach || 0);
      agg.view += Number(b.view || 0);
      agg.trueplay += Number(b.trueplay || 0);
      agg.conversions += Number(b.conversions || 0);
      agg.revenue += Number(b.revenue || 0);
      agg.adjustRevenue += Number(b.adjustRevenue || 0);
      agg.ga4Revenue += Number(b.ga4Revenue || 0);
      agg.ga4Sessions += Number(b.ga4Sessions || 0);
      agg.ga4Purchases += Number(b.ga4Purchases || 0);
      agg.adjustSessions += Number(b.adjustSessions || 0);
      agg.adjustPurchases += Number(b.adjustPurchases || 0);
      agg.adjustInstalls += Number(b.adjustInstalls || 0);
    });
    return agg;
  };

  // Daily last 10 days pivot based on all-time dataset
  const dailyPivot10 = useMemo(() => {
    if (!Array.isArray(metaPivotData) || metaPivotData.length === 0) return { days: [], objectives: [] };

    const toDayKey = (d) => {
      if (!d) return null;
      const dt = new Date(d);
      if (isNaN(dt)) return null;
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const day = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    // Build the last 10 days list (oldest -> newest)
    const days = Array.from({ length: 10 }, (_, idx) => {
      const t = new Date();
      t.setHours(0, 0, 0, 0);
      t.setDate(t.getDate() - (9 - idx));
      const y = t.getFullYear();
      const m = String(t.getMonth() + 1).padStart(2, '0');
      const d = String(t.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    });

    const initBucket = () => ({
      spend: 0, clicks: 0, impressions: 0, conversions: 0,
      reach: 0, view: 0, trueplay: 0,
      revenue: 0, ga4Revenue: 0, ga4Sessions: 0, ga4Purchases: 0,
      adjustRevenue: 0, adjustSessions: 0, adjustPurchases: 0, adjustInstalls: 0
    });

    const objMap = new Map();

    metaPivotData.forEach((row) => {
      const dayKey = toDayKey(row.date || row.dateStart || row.dateStop);
      if (!dayKey || !days.includes(dayKey)) return;
      const objective = row.campaignObjective || 'Unknown';
      const campaignId = row.campaignId;
      const campaignName = row.campaignName || 'Unknown Campaign';

      if (!objMap.has(objective)) {
        objMap.set(objective, { name: formatObjectiveName(objective), totals: new Map(), campaigns: new Map() });
      }
      const o = objMap.get(objective);
      if (!o.totals.has(dayKey)) o.totals.set(dayKey, initBucket());

      if (campaignId) {
        if (!o.campaigns.has(campaignId)) o.campaigns.set(campaignId, { id: campaignId, name: campaignName, totals: new Map() });
        const c = o.campaigns.get(campaignId);
        if (!c.totals.has(dayKey)) c.totals.set(dayKey, initBucket());
        const cb = c.totals.get(dayKey);
        cb.spend += Number(row.spend || 0);
        cb.clicks += Number(row.clicks || 0);
        cb.impressions += Number(row.impressions || 0);
        cb.reach += Number(row.reach || 0);
        cb.view += Number(row.view || 0);
        cb.trueplay += Number(row.trueplay || 0);
        cb.conversions += Number(row.purchaseConversions || row.conversions || 0);
        cb.revenue += Number(row.purchaseValue || row.conversion_value || 0);
        cb.ga4Revenue += Number(row.ga4TotalRevenue || 0);
        cb.ga4Sessions += Number(row.ga4Sessions || 0);
        cb.ga4Purchases += Number(row.ga4Purchases || 0);
        cb.adjustRevenue += Number(row.adjustAllRevenue || 0);
        cb.adjustSessions += Number(row.adjustSessions || 0);
        cb.adjustPurchases += Number(row.adjustPurchases || 0);
        cb.adjustInstalls += Number(row.adjustInstalls || 0);
      }
      const ob = o.totals.get(dayKey);
      ob.spend += Number(row.spend || 0);
      ob.clicks += Number(row.clicks || 0);
      ob.impressions += Number(row.impressions || 0);
      ob.reach += Number(row.reach || 0);
      ob.view += Number(row.view || 0);
      ob.trueplay += Number(row.trueplay || 0);
      ob.conversions += Number(row.purchaseConversions || row.conversions || 0);
      ob.revenue += Number(row.purchaseValue || row.conversion_value || 0);
      ob.ga4Revenue += Number(row.ga4TotalRevenue || 0);
      ob.ga4Sessions += Number(row.ga4Sessions || 0);
      ob.ga4Purchases += Number(row.ga4Purchases || 0);
      ob.adjustRevenue += Number(row.adjustAllRevenue || 0);
      ob.adjustSessions += Number(row.adjustSessions || 0);
      ob.adjustPurchases += Number(row.adjustPurchases || 0);
      ob.adjustInstalls += Number(row.adjustInstalls || 0);
    });

    const objectives = Array.from(objMap.entries()).map(([key, val]) => ({
      id: key,
      name: val.name,
      totals: days.map((d) => ({ dayKey: d, ...(val.totals.get(d) || initBucket()) })),
      campaigns: Array.from(val.campaigns.values()).map((c) => ({
        id: c.id,
        name: c.name,
        totals: days.map((d) => ({ dayKey: d, ...(c.totals.get(d) || initBucket()) })),
      })),
    })).sort((a, b) => {
      const aSpend = a.totals[a.totals.length - 1]?.spend || 0;
      const bSpend = b.totals[b.totals.length - 1]?.spend || 0;
      return bSpend - aSpend;
    });

    return { days, objectives };
  }, [metaPivotData]);
  
  // Kampanya hedeflerine göre verileri tanımlama
  const [data, setData] = useState({
    total: {
      spend: 123500,
      impressions: 4800000,
      clicks: 240000,
      conversions: 4800,
      revenue: 345000,
      roas: 2.79
    },
    objectives: [
      {
        id: 'sales',
        name: 'Sales',
        icon: <ShoppingCart className="w-5 h-5" />,
        color: 'green',
        spend: 68000,
        revenue: 257000,
        roas: 3.78,
        conversions: 3560,
        cpa: 19.1,
        ctr: 6.2,
        share: 55.1,
        trend: 12.4,
        campaigns: 8,
        campaignList: [
          { 
            id: 101, 
            name: "Summer Collection Sales", 
            status: "active", 
            spend: 21200, 
            impressions: 850000, 
            clicks: 48500, 
            conversions: 1620, 
            ctr: 5.7, 
            cpc: 0.44, 
            roas: 4.2 
          },
          { 
            id: 102, 
            name: "End of Season", 
            status: "active", 
            spend: 18400, 
            impressions: 720000, 
            clicks: 42000, 
            conversions: 1140, 
            ctr: 5.8, 
            cpc: 0.44, 
            roas: 3.9 
          },
          { 
            id: 103, 
            name: "Best Sellers Promotion", 
            status: "active", 
            spend: 14600, 
            impressions: 520000, 
            clicks: 28000, 
            conversions: 480, 
            ctr: 5.4, 
            cpc: 0.52, 
            roas: 3.1 
          }
        ]
      },
      {
        id: 'traffic',
        name: 'Traffic',
        icon: <MousePointerClick className="w-5 h-5" />,
        color: 'blue',
        spend: 24500,
        revenue: 58800,
        roas: 2.4,
        conversions: 680,
        cpa: 36.0,
        ctr: 8.5,
        share: 19.8,
        trend: 4.3,
        campaigns: 5,
        campaignList: [
          { 
            id: 201, 
            name: "Website Traffic 2024", 
            status: "active", 
            spend: 9800, 
            impressions: 320000, 
            clicks: 29800, 
            conversions: 240, 
            ctr: 9.3, 
            cpc: 0.33, 
            roas: 2.8 
          },
          { 
            id: 202, 
            name: "Blog Promotion", 
            status: "active", 
            spend: 7400, 
            impressions: 260000, 
            clicks: 21000, 
            conversions: 180, 
            ctr: 8.1, 
            cpc: 0.35, 
            roas: 2.1 
          }
        ]
      },
      {
        id: 'awareness',
        name: 'Brand Awareness',
        icon: <Eye className="w-5 h-5" />,
        color: 'purple',
        spend: 18000,
        revenue: 24300,
        roas: 1.35,
        conversions: 340,
        cpa: 52.9,
        ctr: 3.8,
        share: 14.6,
        trend: -2.7,
        campaigns: 3,
        campaignList: [
          { 
            id: 301, 
            name: "Brand Building 2024", 
            status: "active", 
            spend: 10800, 
            impressions: 620000, 
            clicks: 18600, 
            conversions: 210, 
            ctr: 3.0, 
            cpc: 0.58, 
            roas: 1.44 
          },
          { 
            id: 302, 
            name: "New Audience Reach", 
            status: "active", 
            spend: 7200, 
            impressions: 420000, 
            clicks: 16800, 
            conversions: 130, 
            ctr: 4.0, 
            cpc: 0.43, 
            roas: 1.21 
          }
        ]
      },
      {
        id: 'app',
        name: 'App Installs',
        icon: <Smartphone className="w-5 h-5" />,
        color: 'orange',
        spend: 8600,
        revenue: 4900,
        roas: 0.57,
        conversions: 220,
        cpa: 39.1,
        ctr: 4.1,
        share: 7.0,
        trend: 15.6,
        campaigns: 2
      },
      {
        id: 'engagement',
        name: 'Engagement',
        icon: <MessageSquare className="w-5 h-5" />,
        color: 'pink',
        spend: 4400,
        revenue: 0,
        roas: 0,
        conversions: 0,
        cpa: 0,
        ctr: 5.9,
        share: 3.6,
        trend: -5.2,
        campaigns: 2
      }
    ],
    monthlyTrend: {
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
      sales: [32000, 38000, 45000, 52000, 63000, 68000],
      traffic: [18000, 21000, 19000, 22000, 24000, 24500],
      awareness: [20000, 18000, 16000, 14000, 16000, 18000],
      app: [5000, 6200, 7400, 8100, 8800, 8600],
      engagement: [4800, 4200, 3600, 4100, 4800, 4400]
    }
  });

  // Custom date range handler
  const handleCustomDateChange = (dates) => {
    const [start, end] = dates;
    setCustomStartDate(start);
    setCustomEndDate(end);
    if (start && end) {
      setSelectedDateRange('custom');
      setIsDateDropdownOpen(false);
      console.log('Custom date range selected:', start, end);
    }
  };

  // useEffect'i güncelleyerek custom date desteği ekliyorum
  useEffect(() => {
    // Custom date range seçildiğinde sadece her iki tarih de varsa API çağır
    if (selectedDateRange === 'custom') {
      if (customStartDate && customEndDate) {
        fetchMetaData();
      }
      // Custom date seçilmiş ama tarihler eksikse API çağırma
    } else {
      // Normal date range'ler için direkt çağır
      fetchMetaData();
    }
  }, [selectedDateRange, customStartDate, customEndDate, selectedAccountId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch live status + daily budget from Meta Ads API
  const fetchLiveCampaignData = useCallback(async () => {
    if (!selectedBrand?.id) return;
    setIsLoadingLive(true);
    try {
      const response = await fetch('https://api.glowytics.com/api/tiktok-ads/campaigns-live', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        },
        body: JSON.stringify({ mode: 'campaigns' }),
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok && data.success && Array.isArray(data.campaigns)) {
        const map = {};
        data.campaigns.forEach(c => {
          if (c.id != null) {
            map[String(c.id)] = {
              id: c.id,
              status: c.status || null,
              budget: c.budget != null ? c.budget : null,
              budget_type: c.budget_type || null
            };
          }
        });
        setLiveDataMap(map);
      }
    } catch (e) {
      // Non-fatal: live overlay is optional
      console.warn('[MetaCampaignType] Live data fetch failed:', e);
    } finally {
      setIsLoadingLive(false);
    }
  }, [selectedBrand?.id]);

  // Fetch live data once on mount and whenever brand changes
  useEffect(() => {
    fetchLiveCampaignData();
  }, [fetchLiveCampaignData]);

  // Fetch change history entries
  const fetchChangeHistory = useCallback(async (offset = 0, append = false) => {
    if (!selectedBrand?.id) return;
    setChangeHistoryLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50', offset: String(offset) });
      if (changeHistoryFilter !== 'all') params.set('action', changeHistoryFilter);
      const response = await fetch(`https://api.glowytics.com/api/labs/ads-change-history?${params}`, {
        headers: { 'X-Brand-ID': selectedBrand.id },
        credentials: 'include'
      });
      const result = await response.json();
      if (result.success) {
        setChangeHistoryData(prev => append ? [...prev, ...result.data] : result.data);
        setChangeHistoryTotal(result.total);
      }
    } catch (err) {
      console.error('Failed to fetch change history:', err);
    } finally {
      setChangeHistoryLoading(false);
    }
  }, [selectedBrand?.id, changeHistoryFilter]);

  useEffect(() => {
    if (showChangeHistory) fetchChangeHistory(0);
  }, [showChangeHistory, fetchChangeHistory]);

  // Update Meta campaign status or budget via live API
  const updateEntity = useCallback(async (entityId, field, value, oldValue, entityName, entityType) => {
    setUpdatingId(entityId);
    setOpenStatusDropdown(null);
    try {
      const response = await fetch('https://api.glowytics.com/api/tiktok-ads/campaigns-update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': selectedBrand.id
        },
        body: JSON.stringify({ entityId, field, value, oldValue, entityName, entityType }),
        credentials: 'include'
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || data.details?.error || 'Update failed');
      if (data.success) {
        // Optimistic update in liveDataMap
        setLiveDataMap(prev => {
          const key = String(entityId);
          const existing = prev[key] || {};
          if (field === 'status') {
            return { ...prev, [key]: { ...existing, status: value } };
          } else if (field === 'daily_budget') {
            return { ...prev, [key]: { ...existing, budget: parseInt(value) / 100 } };
          }
          return prev;
        });
      }
    } catch (err) {
      console.error('[MetaCampaignType] Update error:', err);
    } finally {
      setUpdatingId(null);
      setEditBudget(null);
    }
  }, [selectedBrand?.id]);

  const handleSaveBudget = useCallback((campaignId) => {
    if (!editBudget || editBudget.id !== campaignId) return;
    const newValue = parseFloat(editBudget.value);
    const cents = Math.round(newValue * 100);
    if (isNaN(cents) || cents <= 0) return;
    const oldCents = Math.round((editBudget.originalValue || 0) * 100);
    const campaignName = editBudget.name || '';
    setConfirmDialog({
      title: 'Change Budget',
      message: `Are you sure you want to change the daily budget to ₺${newValue.toLocaleString('tr-TR')}?`,
      variant: 'budget',
      onConfirm: () => {
        updateEntity(campaignId, 'daily_budget', cents, String(oldCents), campaignName, 'campaign');
        setConfirmDialog(null);
      }
    });
  }, [editBudget, updateEntity]);

  // API çağrısını custom date desteği ile güncelleyeceğim
  const fetchMetaData = async () => {
    try {
      setLoading(true);
      
      // Get the active brand ID from local storage
      const brandId = localStorage.getItem('currentBrandId');
      
      if (!brandId) {
        console.error('No active brand ID found in localStorage');
        setLoading(false);
        return;
      }
      
      // Define the date range parameter
      const dateRange = selectedDateRange === 'today' ? 'today' :
                        selectedDateRange === 'yesterday' ? 'yesterday' :
                        selectedDateRange === '7' ? 'last7' : 
                        selectedDateRange === '30' ? 'last30' :
                        selectedDateRange === 'thisMonth' ? 'thisMonth' :
                        selectedDateRange === 'lastMonth' ? 'lastMonth' :
                        selectedDateRange === 'custom' ? 'custom' : 'last30';
      
      console.log(`Fetching TikTok data for brand ID: ${brandId} with date range: ${dateRange}`);
      if (dateRange === 'custom') {
        console.log('Custom date range:', customStartDate, customEndDate);
      }
      
      // DEMO: Inject synthetic dataset for demo brands (no brand names in texts)
      if (DEMO_BRAND_IDS_TIKTOK.has(brandId)) {
        const dayList = [];
        const now = new Date();
        const rangeDays = dateRange === 'last7' ? 7
          : dateRange === 'last30' ? 30
          : dateRange === 'yesterday' ? 1
          : dateRange === 'today' ? 1
          : 14;
        for (let i = rangeDays - 1; i >= 0; i--) {
          const d = new Date(now);
          d.setDate(now.getDate() - (dateRange === 'today' ? 0 : i));
          dayList.push(d.toISOString().slice(0, 10));
        }
        const campaigns = [
          { id: 'CMP-AWR-100', name: 'Awareness | Broad Reach', objective: 'AWARENESS', buying: 'AUCTION', status: 'active', bid: 'LOWEST_COST' },
          { id: 'CMP-SAL-200', name: 'Sales | Website Purchases', objective: 'OUTCOME_SALES', buying: 'AUCTION', status: 'active', bid: 'COST_CAP' },
          { id: 'CMP-TRF-300', name: 'Traffic | Link Clicks', objective: 'TRAFFIC', buying: 'AUCTION', status: 'active', bid: 'LOWEST_COST' },
          { id: 'CMP-ENG-400', name: 'Engagement | Posts', objective: 'ENGAGEMENT', buying: 'AUCTION', status: 'paused', bid: 'LOWEST_COST' },
          { id: 'CMP-APP-500', name: 'App Installs | iOS', objective: 'APP_INSTALLS', buying: 'AUCTION', status: 'active', bid: 'LOWEST_COST' },
        ];
        const adsets = [
          { id: 'AS-AWR-1', name: 'Reach - TR All 18-65', cmp: 'CMP-AWR-100' },
          { id: 'AS-SAL-1', name: 'Sales - Remarketing 30D', cmp: 'CMP-SAL-200' },
          { id: 'AS-SAL-2', name: 'Sales - Broad Prospects', cmp: 'CMP-SAL-200' },
          { id: 'AS-TRF-1', name: 'Traffic - Mobile', cmp: 'CMP-TRF-300' },
          { id: 'AS-ENG-1', name: 'Engagement - IG Reels', cmp: 'CMP-ENG-400' },
          { id: 'AS-APP-1', name: 'Installs - iOS TR', cmp: 'CMP-APP-500' },
        ];
        const ads = [
          { id: 'AD-1001', name: 'Creative A', as: 'AS-SAL-1' },
          { id: 'AD-1002', name: 'Creative B', as: 'AS-SAL-2' },
          { id: 'AD-1003', name: 'Creative C', as: 'AS-TRF-1' },
          { id: 'AD-1004', name: 'Creative D', as: 'AS-AWR-1' },
          { id: 'AD-1005', name: 'Creative E', as: 'AS-ENG-1' },
          { id: 'AD-1006', name: 'Creative F', as: 'AS-APP-1' },
        ];
        const rows = [];
        dayList.forEach((day, idx) => {
          campaigns.forEach((c, ci) => {
            const base = 1 + (idx / Math.max(1, dayList.length - 1));
            const spend = Math.round((ci + 1) * 180000 * base) / 100; // ↑ higher spend
            const impr = Math.round((ci + 2) * 35000 * base);        // ↑ higher reach
            const clicks = Math.round(impr * (0.008 + ci * 0.0015)); // ↑ higher CTR
            const conv = c.objective === 'OUTCOME_SALES'
              ? Math.round(clicks * 0.03)                             // ↑ higher CVR
              : c.objective === 'APP_INSTALLS'
              ? Math.round(clicks * 0.09)                             // installs objective converts too
              : Math.round(clicks * 0.006);
            // Add sales value for APP_INSTALLS too (lower AOV than pure sales)
            const value = c.objective === 'OUTCOME_SALES'
              ? Math.round(conv * 650)
              : c.objective === 'APP_INSTALLS'
              ? Math.round(conv * 480)
              : 0;
            const ga4Sessions = Math.round(clicks * 1.1);            // ↑ sessions
            const ga4Purchases = c.objective === 'OUTCOME_SALES'
              ? Math.round(conv * 1.0)
              : c.objective === 'APP_INSTALLS'
              ? Math.round(conv * 0.7)
              : 0;
            const ga4Revenue = (c.objective === 'OUTCOME_SALES' || c.objective === 'APP_INSTALLS')
              ? Math.round(value * 1.05)
              : 0;
            const adjustInstalls = c.objective === 'APP_INSTALLS' ? Math.round(clicks * 0.7) : 0;
            const adjustSessions = Math.round(ga4Sessions * 0.9);
            const adjustPurchases = Math.round(ga4Purchases * 0.85);
            const adjustAllRevenue = Math.round(ga4Revenue * 0.95);
            // choose an adset and ad
            const as = adsets.find(a => a.cmp === c.id) || adsets[0];
            const ad = ads.find(a => a.as === as.id) || ads[0];
            rows.push({
              date: day,
              dateStart: day,
              dateStop: day,
              campaignId: c.id,
              campaignName: c.name,
              campaignObjective: c.objective,
              buyingType: c.buying,
              campaignStatus: c.status,
              bidStrategy: c.bid,
              budgetAmount: Math.round(spend * 3),
              spend,
              impressions: impr,
              clicks,
              purchaseConversions: conv,
              purchaseValue: value,
              ga4Sessions,
              ga4Users: Math.round(ga4Sessions * 0.9),
              ga4NewUsers: Math.round(ga4Sessions * 0.35),
              ga4Purchases,
              ga4TotalRevenue: ga4Revenue,
              adjustInstalls,
              adjustSessions,
              adjustPurchases,
              adjustAllRevenue,
              adsetId: as.id,
              adsetName: as.name,
              adId: ad.id,
              adName: ad.name
            });
          });
        });
        startTransition(() => {
        setMetaData(rows);
        });
        try { localStorage.setItem('creativeDetailData', JSON.stringify([])); } catch {}
        setLoading(false);
        return;
      }
      
      // Build URLs with custom date parameters if needed
      // Switch to unified meta + GA4 performance endpoint (campaign-level)
      let campaignUrl = `https://api.glowytics.com/api/tiktok-ads/unified-campaign-performance?dateRange=${dateRange}`;
      let creativeUrl = `https://api.glowytics.com/api/tiktok-ads/creative-details?dateRange=${dateRange}`;
      
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        const startDateStr = customStartDate.toISOString().split('T')[0];
        const endDateStr = customEndDate.toISOString().split('T')[0];
        campaignUrl += `&startDate=${startDateStr}&endDate=${endDateStr}`;
        creativeUrl += `&startDate=${startDateStr}&endDate=${endDateStr}`;
      }
      
      // Make both API calls in parallel for better performance
      const [response, creativeResponse] = await Promise.all([
        axios.get(campaignUrl, {
        withCredentials: true,
        headers: {
          'X-Brand-ID': brandId
        }
        }),
        axios.get(creativeUrl, {
          withCredentials: true,
          headers: {
            'X-Brand-ID': brandId
          }
        }).catch(err => {
          console.error('Error fetching creative details:', err);
          return { data: [] };
        })
      ]);
      
      // Log the data to console
      console.log('Unified TikTok + GA4 campaign performance data:', response.data);
      
      // Normalize API response to camelCase + include GA4 metrics
      const normalizedData = Array.isArray(response.data) ? response.data.map(item => ({
        accountId: item.account_id || null,
        accountName: item.account_name || item.account_id || null,
        date: item.date,
        dateStart: item.date_start,
        dateStop: item.date_stop,
        campaignId: item.campaign_id,
        campaignName: item.campaign_name,
        campaignObjective: item.campaign_objective,
        buyingType: item.buying_type,
        campaignStatus: item.campaign_status,
        bidStrategy: item.bid_strategy,
        // TikTok budget is already in currency units (no /100 scaling)
        budgetAmount: Number(item.budget_amount || 0),
        spend: Number(item.spend || 0),
        impressions: Number(item.impressions || 0),
        clicks: Number(item.clicks || 0),
        reach: Number(item.reach || 0),
        view: Number(item.view || 0),
        trueplay: Number(item.trueplay || 0),
        purchaseConversions: Number(item.conversions || 0),
        purchaseValue: Number(item.conversion_value || 0),
        // GA4 metrics
        ga4Sessions: Number(item.ga4_sessions || 0),
        ga4Users: Number(item.ga4_users || 0),
        ga4NewUsers: Number(item.ga4_new_users || 0),
        ga4Purchases: Number(item.ga4_purchases || 0),
        ga4TotalRevenue: Number(item.ga4_total_revenue || 0),
        // Adjust metrics
        adjustInstalls: Number(item.adjust_installs || 0),
        adjustSessions: Number(item.adjust_sessions || 0),
        adjustPurchases: Number(item.adjust_purchase_events || 0),
        adjustAllRevenue: Number(item.adjust_all_revenue || 0)
      })) : [];
      
      // Store the data in state with startTransition for non-blocking UI
      startTransition(() => {
      setMetaData(normalizedData);
      });

      // Process creative details response
      if (creativeResponse.data && creativeResponse.data.length > 0) {
        console.log('Creative details data from endpoint:', creativeResponse.data);
        console.log(`Retrieved ${creativeResponse.data.length} creative records`);
          console.log('Sample creative record (first):', creativeResponse.data[0]);
          console.log('Available fields:', Object.keys(creativeResponse.data[0]));
          
          // Video kreatifleri analizi
          const videoCreatives = creativeResponse.data.filter(item => item.is_video === true);
          console.log(`Video creatives: ${videoCreatives.length} records`);
          
          // Image kreatifleri analizi
          const imageCreatives = creativeResponse.data.filter(item => item.is_video === false);
          console.log(`Image creatives: ${imageCreatives.length} records`);
        }
        
        // LocalStorage'a kaydet - diğer sayfalarda kullanılabilir
      try {
        localStorage.setItem('creativeDetailData', JSON.stringify(creativeResponse.data || []));
      } catch (storageError) {
        console.warn('Could not save creative data to localStorage:', storageError);
      }
      
      // Set loading to false - no artificial delay needed
        setLoading(false);
      
    } catch (error) {
      console.error('Error fetching TikTok campaign data:', error);
        setLoading(false);
    }
  };

  // Fetch all-time data for the monthly pivot only once (independent of page filter)
  useEffect(() => {
    const fetchAllForPivot = async () => {
      try {
        const brandId = localStorage.getItem('currentBrandId');
        if (!brandId) return;
        // DEMO: all-time pivot data synthesis
        if (DEMO_BRAND_IDS_TIKTOK.has(brandId)) {
          const months = [];
          const now = new Date();
          for (let i = 11; i >= 0; i--) {
            const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
            months.push(d.toISOString().slice(0, 10));
          }
          const cmp = [
            { id: 'CMP-AWR-100', name: 'Awareness | Broad Reach', objective: 'AWARENESS', buying: 'AUCTION', status: 'active', bid: 'LOWEST_COST' },
            { id: 'CMP-SAL-200', name: 'Sales | Website Purchases', objective: 'OUTCOME_SALES', buying: 'AUCTION', status: 'active', bid: 'COST_CAP' },
            { id: 'CMP-TRF-300', name: 'Traffic | Link Clicks', objective: 'TRAFFIC', buying: 'AUCTION', status: 'active', bid: 'LOWEST_COST' },
          ];
          const data = [];
          months.forEach((m, idx) => {
            cmp.forEach((c, ci) => {
              const factor = 1 + idx * 0.08;
              const spend = Math.round((ci + 1) * 360000 * factor) / 100;   // ↑ spend
              const impr = Math.round((ci + 2) * 90000 * factor);           // ↑ impressions
              const clicks = Math.round(impr * (0.012 + ci * 0.0018));      // ↑ CTR
              const conv = c.objective === 'OUTCOME_SALES'
                ? Math.round(clicks * 0.035)
                : c.objective === 'APP_INSTALLS'
                ? Math.round(clicks * 0.015) // App installs driving sales too
                : Math.round(clicks * 0.006);
              const value = c.objective === 'OUTCOME_SALES'
                ? Math.round(conv * 720)
                : c.objective === 'APP_INSTALLS'
                ? Math.round(conv * 520)
                : 0; // ↑ AOVs
              data.push({
                date: m, dateStart: m, dateStop: m,
                campaignId: c.id, campaignName: c.name, campaignObjective: c.objective,
                buyingType: c.buying, campaignStatus: c.status, bidStrategy: c.bid,
                budgetAmount: Math.round(spend * 3),
                spend, impressions: impr, clicks,
                purchaseConversions: conv, purchaseValue: value,
                ga4Sessions: Math.round(clicks * 1.15),
                ga4Users: Math.round(clicks * 0.9),
                ga4NewUsers: Math.round(clicks * 0.4),
                ga4Purchases: (c.objective === 'OUTCOME_SALES')
                  ? Math.round(conv * 1.05)
                  : (c.objective === 'APP_INSTALLS' ? Math.round(conv * 0.75) : 0),
                ga4TotalRevenue: (c.objective === 'OUTCOME_SALES' || c.objective === 'APP_INSTALLS')
                  ? Math.round(value * 1.08)
                  : 0,
                adjustInstalls: 0, adjustSessions: 0, adjustPurchases: 0, adjustAllRevenue: 0
              });
            });
          });
          startTransition(() => {
          setMetaPivotData(data);
          });
          return;
        }

        const url = `https://api.glowytics.com/api/tiktok-ads/unified-campaign-performance?dateRange=all`;
        const response = await axios.get(url, {
          withCredentials: true,
          headers: { 'X-Brand-ID': brandId },
        });
        const normalized = Array.isArray(response.data) ? response.data.map(item => ({
          date: item.date,
          dateStart: item.date_start,
          dateStop: item.date_stop,
          campaignId: item.campaign_id,
          campaignName: item.campaign_name,
          campaignObjective: item.campaign_objective,
          buyingType: item.buying_type,
          campaignStatus: item.campaign_status,
          bidStrategy: item.bid_strategy,
          budgetAmount: Number(item.budget_amount || 0) / 100,
          spend: Number(item.spend || 0),
          impressions: Number(item.impressions || 0),
          clicks: Number(item.clicks || 0),
          reach: Number(item.reach || 0),
          view: Number(item.view || 0),
          trueplay: Number(item.trueplay || 0),
          purchaseConversions: Number(item.conversions || 0),
          purchaseValue: Number(item.conversion_value || 0),
          ga4Sessions: Number(item.ga4_sessions || 0),
          ga4Users: Number(item.ga4_users || 0),
          ga4NewUsers: Number(item.ga4_new_users || 0),
          ga4Purchases: Number(item.ga4_purchases || 0),
          ga4TotalRevenue: Number(item.ga4_total_revenue || 0),
          // Adjust metrics
          adjustInstalls: Number(item.adjust_installs || 0),
          adjustSessions: Number(item.adjust_sessions || 0),
          adjustPurchases: Number(item.adjust_purchase_events || 0),
          adjustAllRevenue: Number(item.adjust_all_revenue || 0)
        })) : [];
        startTransition(() => {
        setMetaPivotData(normalized);
        });
      } catch (e) {
        console.error('Error fetching all-time pivot data:', e);
      }
    };
    fetchAllForPivot();
  }, []);

  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) return '₺0';
    return '₺' + Number(value).toLocaleString('tr-TR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const formatCurrencyDecimals = (value) => {
    if (value === null || value === undefined || isNaN(value)) return '₺0.00';
    return '₺' + Number(value).toLocaleString('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined || isNaN(value)) return '0';
    const numValue = Number(value);
    if (numValue >= 1000000) return `${(numValue / 1000000).toFixed(1)}M`;
    if (numValue >= 1000) return `${(numValue / 1000).toFixed(1)}K`;
    return numValue.toString();
  };

  // Short number formatting for Campaign Objective Summary (custom rules):
  // - <= 99,999 show full number
  // - >= 100,000 show K
  // - 1,000,500 -> 1M, 1,250,000 -> 1.25M
  const formatShortNumber = (value) => {
    if (value === null || value === undefined || isNaN(value)) return '0';
    const num = Number(value);
    const abs = Math.abs(num);
    if (abs < 100000) {
      return num.toLocaleString('tr-TR', { maximumFractionDigits: 0 });
    }
    // Promote to 1M if >= 999,500 to avoid "1000K"
    if (abs >= 999500) {
      const millions = num / 1_000_000;
      const rounded = Math.round(millions * 100) / 100; // 2 decimals
      // 1,000,500 -> 1M behavior
      if (rounded < 1.05) return `${Math.sign(num) < 0 ? '-' : ''}1M`;
      return `${Math.sign(num) < 0 ? '-' : ''}${rounded.toString().replace('.', ',')}M`;
    }
    if (abs >= 1_000_000) {
      const millions = num / 1_000_000;
      const rounded = Math.round(millions * 100) / 100;
      if (rounded < 1.05) return `${Math.sign(num) < 0 ? '-' : ''}1M`;
      return `${Math.sign(num) < 0 ? '-' : ''}${rounded.toString().replace('.', ',')}M`;
    }
    // 100,000 .. < 1,000,000 => K (integer)
    const k = Math.round(num / 1000);
    return `${k.toLocaleString('tr-TR')}K`;
  };

  const formatShortCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) return '₺0';
    const sign = Number(value) < 0 ? '-' : '';
    const n = Math.abs(Number(value));
    if (n < 100000) {
      return `${sign}₺${n.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`;
    }
    // Use the short number rules but without sign/symbol duplication
    const short = formatShortNumber(n);
    return `${sign}₺${short}`;
  };

  const getColorClasses = (objectiveId) => {
    switch (objectiveId) {
      case 'sales':
        return {
          bgHeader: 'bg-green-50',
          bgIcon: 'bg-green-100',
          textIcon: 'text-green-600',
          dotColor: 'bg-green-500',
        };
      case 'traffic':
        return {
          bgHeader: 'bg-blue-50',
          bgIcon: 'bg-blue-100',
          textIcon: 'text-blue-600',
          dotColor: 'bg-blue-500',
        };
      case 'awareness':
        return {
          bgHeader: 'bg-purple-50',
          bgIcon: 'bg-purple-100',
          textIcon: 'text-purple-600',
          dotColor: 'bg-purple-500',
        };
      case 'app':
        return {
          bgHeader: 'bg-orange-50',
          bgIcon: 'bg-orange-100',
          textIcon: 'text-orange-600',
          dotColor: 'bg-orange-500',
        };
      case 'engagement':
        return {
          bgHeader: 'bg-pink-50',
          bgIcon: 'bg-pink-100',
          textIcon: 'text-pink-600',
          dotColor: 'bg-pink-500',
        };
      default:
        return {
          bgHeader: 'bg-gray-50',
          bgIcon: 'bg-gray-100',
          textIcon: 'text-gray-600',
          dotColor: 'bg-gray-500',
        };
    }
  };

  const getFilteredObjectives = () => {
    if (selectedObjective === 'all') {
      return data.objectives;
    }
    return data.objectives.filter(obj => obj.id === selectedObjective);
  };

  // Function to get icon and color for campaign objective
  const getObjectiveIconAndColor = (objectiveId) => {
    const objectiveMappings = {
      'sales': {
        icon: <ShoppingCart className="w-5 h-5" />,
        color: 'green',
        bgColor: '#22c55e'
      },
      'traffic': {
        icon: <MousePointerClick className="w-5 h-5" />,
        color: 'blue',
        bgColor: '#3b82f6'
      },
      'awareness': {
        icon: <Eye className="w-5 h-5" />,
        color: 'purple',
        bgColor: '#a855f7'
      },
      'app_installs': {
        icon: <Smartphone className="w-5 h-5" />,
        color: 'orange',
        bgColor: '#f97316'
      },
      'engagement': {
        icon: <MessageSquare className="w-5 h-5" />,
        color: 'pink',
        bgColor: '#ec4899'
      },
      'leads': {
        icon: <Users className="w-5 h-5" />,
        color: 'indigo',
        bgColor: '#6366f1'
      },
      'reach': {
        icon: <Share2 className="w-5 h-5" />,
        color: 'teal',
        bgColor: '#14b8a6'
      },
      'conversions': {
        icon: <Target className="w-5 h-5" />,
        color: 'rose',
        bgColor: '#f43f5e'
      },
      'messages': {
        icon: <MessageSquare className="w-5 h-5" />,
        color: 'cyan',
        bgColor: '#06b6d4'
      },
      'unknown': {
        icon: <Globe className="w-5 h-5" />,
        color: 'gray',
        bgColor: '#6b7280'
      }
    };
    
    // Normalize objectiveId by converting to lowercase and replacing spaces with underscores
    const normalizedId = String(objectiveId).toLowerCase().replace(/\s+/g, '_');
    
    // Try to find a direct match
    if (objectiveMappings[normalizedId]) {
      return objectiveMappings[normalizedId];
    }
    
    // If not found, try to match based on keywords
    for (const [key, value] of Object.entries(objectiveMappings)) {
      if (normalizedId.includes(key)) {
        return value;
      }
    }
    
    // Default to unknown
    return objectiveMappings.unknown;
  };

  if (loading) {
    return <LoadingScreen />;
  }

    return (
    <div className="p-6 space-y-6 overflow-x-hidden w-full max-w-screen-2xl mx-auto">
      {/* Confirmation Dialog */}
      {confirmDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setConfirmDialog(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-100 w-full max-w-md mx-4 overflow-hidden">
            <div className={`px-6 pt-6 pb-4 flex items-start gap-4 ${
              confirmDialog.variant === 'pause' ? 'bg-orange-50/50' :
              confirmDialog.variant === 'enable' ? 'bg-green-50/50' :
              'bg-blue-50/50'
            }`}>
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                confirmDialog.variant === 'pause' ? 'bg-orange-100' :
                confirmDialog.variant === 'enable' ? 'bg-green-100' :
                'bg-blue-100'
              }`}>
                <AlertTriangle className={`w-5 h-5 ${
                  confirmDialog.variant === 'pause' ? 'text-orange-600' :
                  confirmDialog.variant === 'enable' ? 'text-green-600' :
                  'text-blue-600'
                }`} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{confirmDialog.title}</h3>
                <p className="mt-1 text-sm text-gray-600">{confirmDialog.message}</p>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50/80 flex items-center justify-end gap-3">
              <button
                onClick={() => setConfirmDialog(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDialog.onConfirm}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors focus:outline-none focus:ring-2 ${
                  confirmDialog.variant === 'pause' ? 'bg-orange-500 hover:bg-orange-600 focus:ring-orange-200' :
                  confirmDialog.variant === 'enable' ? 'bg-green-500 hover:bg-green-600 focus:ring-green-200' :
                  'bg-blue-500 hover:bg-blue-600 focus:ring-blue-200'
                }`}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header with Filters */}
      <div className="flex justify-between items-center bg-white p-5 rounded-2xl shadow-sm">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl">
            <img 
              src="/tiktokv4.svg" 
              alt="TikTok Ads Logo" 
              className="w-8 h-8 object-contain"
            />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">Campaign Objectives</h1>
            <p className="text-sm text-gray-500 mt-1">Performance by campaign type</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <AccountSwitcher platform="TIKTOK_ADS" />
          {metaAccountOptions.length >= 2 && (
            <select
              value={metaAccountFilter}
              onChange={(e) => setMetaAccountFilter(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-xl text-sm bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[160px]"
            >
              <option value="all">All Accounts</option>
              {metaAccountOptions.map(id => (
                <option key={id} value={id}>{accountNameMap[id] || id}</option>
              ))}
            </select>
          )}
          <select
            className="px-4 py-2 border border-gray-200 rounded-xl text-sm bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[140px]"
            value={selectedDateRange}
            onChange={(e) => {
              setSelectedDateRange(e.target.value);
              // Custom seçildiğinde tarihler sıfırla
              if (e.target.value === 'custom') {
                setCustomStartDate(null);
                setCustomEndDate(null);
              }
            }}
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
            <option value="thisMonth">This Month</option>
            <option value="lastMonth">Last Month</option>
            <option value="custom">Custom Range</option>
          </select>

          {selectedDateRange === 'custom' && (
            <div className="relative">
              <button
                onClick={() => setIsCustomDateOpen(!isCustomDateOpen)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <Calendar className="w-4 h-4" />
                {customStartDate && customEndDate ? (
                  `${customStartDate.toLocaleDateString()} - ${customEndDate.toLocaleDateString()}`
                ) : (
                  'Select Date Range'
                )}
              </button>
              {isCustomDateOpen && (
                <div className="absolute right-0 mt-2 z-50">
                  <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4">
                    <DatePicker
                      selected={customStartDate}
                      onChange={handleCustomDateChange}
                      startDate={customStartDate}
                      endDate={customEndDate}
                      selectsRange
                      inline
                      calendarClassName="bg-white"
                      monthClassName="text-gray-800"
                      dayClassName={date =>
                        date >= customStartDate && date <= customEndDate
                          ? "bg-blue-50 text-blue-600"
                          : undefined
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={() => setShowChangeHistory(true)}
              className="flex items-center gap-2 px-3 py-2 bg-white border rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
              title="Change History"
            >
              <History className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium">History</span>
            </button>
            <button className="p-2 text-gray-600 hover:text-blue-600 rounded-xl hover:bg-blue-50 transition-colors">
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <Ga4WebOnlyNote />

      {/* Key Metrics */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${hasAnyAdjustKpi ? 'lg:grid-cols-6' : 'lg:grid-cols-4'} gap-4`}>
        <Card className="p-4 rounded-xl">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm mb-1">Total Spend</p>
              <p className="text-xl font-bold mb-1">
                {loading ? (
                  <span className="inline-block w-20 h-8 bg-gray-100 animate-pulse rounded"></span>
                ) : (
                  formatCurrency(computedMetrics.totalSpend || 0)
                )}
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <img src="/tiktokv4.svg" alt="TikTok" className="h-5 w-5 object-contain" />
            </div>
          </div>
        </Card>
        
        <Card className="p-4 rounded-xl">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm mb-1">Revenue</p>
              <p className="text-xl font-bold mb-1">
                {loading ? (
                  <span className="inline-block w-20 h-8 bg-gray-100 animate-pulse rounded"></span>
                ) : (
                  formatCurrency(computedMetrics.totalPurchaseValue || 0)
                )}
              </p>
              
            </div>
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <img src="/tiktokv4.svg" alt="TikTok" className="h-5 w-5 object-contain" />
            </div>
          </div>
        </Card>

        {/* GA Rev. */}
        <Card className="p-4 rounded-xl">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm mb-1">GA Rev.</p>
              <p className="text-xl font-bold mb-1">
                {loading ? (
                  <span className="inline-block w-20 h-8 bg-gray-100 animate-pulse rounded"></span>
                ) : (
                  formatCurrency(computedMetrics.totalGa4Revenue || 0)
                )}
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <img src="/GA4logoicon.svg" alt="GA4" className="h-5 w-5 object-contain" />
            </div>
          </div>
        </Card>
        
        {/* GA4 Sessions */}
        <Card className="p-4 rounded-xl">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm mb-1">GA4 Sessions</p>
              <p className="text-xl font-bold mb-1">
                {loading ? (
                  <span className="inline-block w-20 h-8 bg-gray-100 animate-pulse rounded"></span>
                ) : (
                  formatNumber(computedMetrics.totalGa4Sessions || 0)
                )}
              </p>
              
            </div>
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <img src="/GA4logoicon.svg" alt="GA4" className="h-5 w-5 object-contain" />
            </div>
          </div>
        </Card>

        {/* GA4 Purchases */}
        <Card className="p-4 rounded-xl">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm mb-1">GA4 Purchases</p>
              <p className="text-xl font-bold mb-1">
                {loading ? (
                  <span className="inline-block w-20 h-8 bg-gray-100 animate-pulse rounded"></span>
                ) : (
                  formatNumber(computedMetrics.totalGa4Purchases || 0)
                )}
              </p>
              
            </div>
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <img src="/GA4logoicon.svg" alt="GA4" className="h-5 w-5 object-contain" />
            </div>
          </div>
        </Card>

        <Card className="p-4 rounded-xl">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm mb-1">ROAS</p>
              <p className="text-xl font-bold mb-1">
                {loading ? (
                  <span className="inline-block w-20 h-8 bg-gray-100 animate-pulse rounded"></span>
                ) : (
                  `${computedMetrics.roas.toFixed(1)}x`
                )}
              </p>
              
            </div>
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <img src="/tiktokv4.svg" alt="TikTok" className="h-5 w-5 object-contain" />
            </div>
          </div>
        </Card>
        
        <Card className="p-4 rounded-xl">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm mb-1">Impressions</p>
              <p className="text-xl font-bold mb-1">
                {loading ? (
                  <span className="inline-block w-20 h-8 bg-gray-100 animate-pulse rounded"></span>
                ) : (
                  formatNumber(computedMetrics.totalImpressions || 0)
                )}
              </p>
              
            </div>
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <img src="/tiktokv4.svg" alt="TikTok" className="h-5 w-5 object-contain" />
            </div>
          </div>
        </Card>
        
        <Card className="p-4 rounded-xl">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm mb-1">Conversions</p>
              <p className="text-xl font-bold mb-1">
                {loading ? (
                  <span className="inline-block w-20 h-8 bg-gray-100 animate-pulse rounded"></span>
                ) : (
                  formatNumber(computedMetrics.totalPurchaseConversions || 0)
                )}
              </p>
              
            </div>
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <img src="/tiktokv4.svg" alt="TikTok" className="h-5 w-5 object-contain" />
            </div>
          </div>
        </Card>

        {showAdjRevCol && (
          <Card className="p-4 rounded-xl">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-500 text-sm mb-1">Adjust Revenue</p>
                <p className="text-xl font-bold mb-1">
                  {loading ? (
                    <span className="inline-block w-20 h-8 bg-gray-100 animate-pulse rounded"></span>
                  ) : (
                    formatCurrency(computedMetrics.totalAdjustRevenue || 0)
                  )}
                </p>
              </div>
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                <img src="/adjustlogopng.png" alt="Adjust" className="h-5 w-5 object-contain" />
              </div>
            </div>
          </Card>
        )}

        {showAdjSessCol && (
          <Card className="p-4 rounded-xl">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-500 text-sm mb-1">Adjust Sessions</p>
                <p className="text-xl font-bold mb-1">
                  {loading ? (
                    <span className="inline-block w-20 h-8 bg-gray-100 animate-pulse rounded"></span>
                  ) : (
                    formatNumber(computedMetrics.totalAdjustSessions || 0)
                  )}
                </p>
              </div>
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                <img src="/adjustlogopng.png" alt="Adjust" className="h-5 w-5 object-contain" />
              </div>
            </div>
          </Card>
        )}

        {showAdjPurchCol && (
          <Card className="p-4 rounded-xl">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-500 text-sm mb-1">Adjust Purchases</p>
                <p className="text-xl font-bold mb-1">
                  {loading ? (
                    <span className="inline-block w-20 h-8 bg-gray-100 animate-pulse rounded"></span>
                  ) : (
                    formatNumber(computedMetrics.totalAdjustPurchases || 0)
                  )}
                </p>
              </div>
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                <img src="/adjustlogopng.png" alt="Adjust" className="h-5 w-5 object-contain" />
              </div>
            </div>
          </Card>
        )}

        {showAdjInstCol && (
          <Card className="p-4 rounded-xl">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-500 text-sm mb-1">Adjust Installs</p>
                <p className="text-xl font-bold mb-1">
                  {loading ? (
                    <span className="inline-block w-20 h-8 bg-gray-100 animate-pulse rounded"></span>
                  ) : (
                    formatNumber(computedMetrics.totalAdjustInstalls || 0)
                  )}
                </p>
              </div>
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                <img src="/adjustlogopng.png" alt="Adjust" className="h-5 w-5 object-contain" />
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Performance Trends + Budget Utilization */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Performance Chart - 2 cols */}
        <Card className="rounded-xl overflow-hidden lg:col-span-2">
          <div className="p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
            <h3 className="text-sm font-medium text-gray-800">Performance Trends</h3>
            <div className="flex items-center gap-2">
              {displayDailyData && (
                <>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-50 border border-blue-100 text-xs font-medium text-blue-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                    Spend ₺{(displayDailyData.spend?.reduce((a, b) => a + (b || 0), 0) || 0).toLocaleString('tr-TR')}
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-50 border border-emerald-100 text-xs font-medium text-emerald-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    Revenue ₺{(displayDailyData.revenue?.reduce((a, b) => a + (b || 0), 0) || 0).toLocaleString('tr-TR')}
                  </span>
                </>
              )}
            </div>
          </div>

          <div className="px-4 pb-4">
            <div className="h-[220px] md:h-[260px] -mx-1">
              {displayDailyData ? (
                <LineChart
                  data={{
                    labels: displayDailyData.labels,
                    datasets: [
                      {
                        label: 'Spend',
                        data: displayDailyData.spend,
                        yAxisID: 'y1',
                        borderColor: '#3b82f6',
                        backgroundColor: function(context) {
                          const chart = context.chart;
                          const { ctx, chartArea } = chart;
                          if (!chartArea) return 'transparent';
                          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                          gradient.addColorStop(0, 'rgba(59, 130, 246, 0.35)');
                          gradient.addColorStop(1, 'rgba(59, 130, 246, 0)');
                          return gradient;
                        },
                        fill: true,
                        tension: 0.35,
                        borderWidth: 2.5,
                        pointRadius: 0,
                        pointHoverRadius: 6,
                        pointHoverBackgroundColor: '#fff',
                        pointHoverBorderColor: '#3b82f6',
                        pointHoverBorderWidth: 2,
                        spanGaps: true
                      },
                      {
                        label: 'Revenue',
                        data: displayDailyData.revenue,
                        yAxisID: 'y',
                        borderColor: '#10b981',
                        backgroundColor: function(context) {
                          const chart = context.chart;
                          const { ctx, chartArea } = chart;
                          if (!chartArea) return 'transparent';
                          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                          gradient.addColorStop(0, 'rgba(16, 185, 129, 0.35)');
                          gradient.addColorStop(1, 'rgba(16, 185, 129, 0)');
                          return gradient;
                        },
                        fill: true,
                        tension: 0.35,
                        borderWidth: 2.5,
                        pointRadius: 0,
                        pointHoverRadius: 6,
                        pointHoverBackgroundColor: '#fff',
                        pointHoverBorderColor: '#10b981',
                        pointHoverBorderWidth: 2,
                        spanGaps: true
                      }
                    ]
                  }}
                  height="100%"
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: { padding: { top: 4, right: 12, bottom: 4, left: 12 } },
                    interaction: { mode: 'index', intersect: false },
                    scales: {
                      x: {
                        grid: { display: false },
                        border: { display: false },
                        ticks: { maxRotation: 0, maxTicksLimit: 8, font: { size: 10 }, color: '#94a3b8' }
                      },
                      y: {
                        position: 'left',
                        beginAtZero: false,
                        border: { display: false },
                        ticks: {
                          callback: (value) => '₺' + (value / 1000) + 'K',
                          font: { size: 10 },
                          color: '#10b981',
                          maxTicksLimit: 5
                        },
                        grid: { color: 'rgba(16, 185, 129, 0.15)', drawTicks: false }
                      },
                      y1: {
                        position: 'right',
                        beginAtZero: false,
                        border: { display: false },
                        grid: { drawOnChartArea: false },
                        ticks: {
                          callback: (value) => '₺' + (value / 1000) + 'K',
                          font: { size: 10 },
                          color: '#3b82f6',
                          maxTicksLimit: 5
                        }
                      }
                    },
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        backgroundColor: 'rgba(255,255,255,0.98)',
                        titleColor: '#1e293b',
                        bodyColor: '#475569',
                        borderColor: '#e2e8f0',
                        borderWidth: 1,
                        padding: 10,
                        boxPadding: 4,
                        cornerRadius: 8,
                        titleFont: { size: 12, weight: '600' },
                        bodyFont: { size: 11 },
                        usePointStyle: true,
                        callbacks: {
                          title: (items) => items[0]?.label || '',
                          label: (context) => {
                            const label = context.dataset.label || '';
                            const value = context.raw || 0;
                            return `${label}: ₺${Math.round(value).toLocaleString('tr-TR')}`;
                          }
                        }
                      }
                    }
                  }}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full">
                  <BarChart3 className="w-12 h-12 text-gray-300 mb-2" />
                  <h3 className="text-base font-medium text-gray-600 mb-1">No data available</h3>
                  <p className="text-sm text-gray-400 text-center max-w-xs">
                    No performance trend data for the selected date range.
                  </p>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Budget Utilization Card - 1 col */}
        <Card className="rounded-xl overflow-hidden flex flex-col min-h-[280px]">
          <div className="flex justify-between items-center p-4 border-b flex-shrink-0">
            <h3 className="text-sm font-medium text-gray-800">Budget Utilization</h3>
            <div className="p-1.5 bg-blue-50 rounded-full border border-blue-100">
              <DollarSign className="w-4 h-4 text-blue-600" />
            </div>
          </div>

          <div className="p-4 flex-1 flex flex-col">
            {(computedMetrics.totalSpend !== undefined && computedMetrics.totalSpend !== null) ? (
              (() => {
                const today = new Date();
                const currentYear = today.getFullYear();
                const currentMonth = today.getMonth();
                const currentDay = today.getDate();
                const totalDaysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
                const remainingDays = totalDaysInMonth - currentDay;
                const totalSpent = computedMetrics.totalSpend || 0;
                const spentPct = Math.min(Math.round((totalSpent / targetBudget) * 100), 100);
                const remainingBudget = Math.max(0, targetBudget - totalSpent);
                const dailyTarget = remainingDays > 0 ? remainingBudget / remainingDays : 0;
                const ringColor = spentPct > 80 ? '#ef4444' : spentPct > 50 ? '#f59e0b' : '#3b82f6';
                const circumference = 2 * Math.PI * 36;
                const strokeDash = (spentPct / 100) * circumference;

                const formatBudgetNum = (val) => '₺' + Math.round(val).toLocaleString('tr-TR');

                return (
                  <div className="flex-1 flex flex-col justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="relative flex-shrink-0">
                        <svg className="w-20 h-20 -rotate-90" viewBox="0 0 84 84">
                          <circle cx="42" cy="42" r="36" fill="none" stroke="#f3f4f6" strokeWidth="6" />
                          <circle cx="42" cy="42" r="36" fill="none" stroke={ringColor} strokeWidth="6" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={circumference - strokeDash} className="transition-all duration-500" />
                        </svg>
                        <span className="absolute inset-0 flex items-center justify-center text-sm font-bold tabular-nums" style={{ color: ringColor }}>{spentPct}%</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] uppercase font-medium text-gray-400 tracking-wide">{today.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</p>
                        <p className="text-2xl font-bold tabular-nums text-gray-900 mt-0.5">{formatBudgetNum(totalSpent)}</p>
                        <p className="text-[11px] text-gray-500 mt-1">
                          {isEditingBudget ? (
                            <span className="flex items-center gap-1">
                              <span>₺</span>
                              <input
                                type="text"
                                className="w-20 border border-blue-300 rounded px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                                defaultValue={targetBudget.toLocaleString('tr-TR')}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    const value = e.target.value.replace(/[^0-9]/g, '');
                                    const numValue = parseInt(value, 10);
                                    if (!isNaN(numValue) && numValue > 0) setTargetBudget(numValue);
                                    setIsEditingBudget(false);
                                  } else if (e.key === 'Escape') setIsEditingBudget(false);
                                }}
                                onBlur={() => setIsEditingBudget(false)}
                                autoFocus
                              />
                            </span>
                          ) : (
                            <span className="cursor-pointer hover:text-blue-600 transition-colors" onClick={() => setIsEditingBudget(true)}>
                              of {formatBudgetNum(targetBudget)} target
                            </span>
                          )}
                        </p>
                      </div>
                    </div>

                    <div className="flex-shrink-0">
                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${spentPct}%`, backgroundColor: ringColor }}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 flex-shrink-0">
                      <div className="rounded-xl bg-gray-50/80 border border-gray-100 p-3">
                        <p className="text-[10px] uppercase font-medium text-gray-400 tracking-wide">Daily target</p>
                        <p className="text-lg font-bold tabular-nums text-gray-900 mt-0.5">{formatBudgetNum(dailyTarget)}</p>
                      </div>
                      <div className="rounded-xl bg-gray-50/80 border border-gray-100 p-3">
                        <p className="text-[10px] uppercase font-medium text-gray-400 tracking-wide">Remaining</p>
                        <p className="text-lg font-bold tabular-nums text-gray-900 mt-0.5">{formatBudgetNum(remainingBudget)}</p>
                        <p className="text-[11px] text-gray-500 mt-0.5">{remainingDays} days left</p>
                      </div>
                    </div>
                  </div>
                );
              })()
            ) : (
              <div className="flex flex-col items-center justify-center flex-1">
                <DollarSign className="w-12 h-12 text-gray-400 mb-2" />
                <h3 className="text-lg font-medium text-gray-700 mb-1">No data available</h3>
                <p className="text-sm text-gray-500 text-center max-w-md">
                  No budget data found for the selected date range.
                </p>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Campaign Objective Summary Table */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Campaign Objective Summary</h2>
          <p className="text-sm text-gray-500 mt-1">Performance overview by campaign objective</p>
        </div>
        <div className="overflow-x-auto w-full">
          <table className="min-w-[1200px] w-full border border-gray-200">
            <thead className="bg-gray-50">
              <tr className="divide-x divide-gray-200">
                    <th className="px-4 py-2 text-left text-[11px] font-medium text-gray-500 tracking-wider">Objective</th>
                    <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 tracking-wider">Budget</th>
                    <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 tracking-wider">Spend</th>
                    {/* Meta Revenue */}
                    <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 tracking-wider">
                      <span className="inline-flex items-center gap-1">
                        <img src="/tiktokv4.svg" alt="TikTok" className="h-3 w-auto" />
                        Rev.
                      </span>
                    </th>
                    {/* GA4 Revenue */}
                    <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 tracking-wider">
                      <span className="inline-flex items-center gap-1">
                        <img src="/GA4logoicon.svg" alt="GA4" className="h-3 w-auto" />
                        Rev.
                      </span>
                    </th>
                    {/* Adjust Revenue */}
                    {showAdjRevCol && (
                      <>
                        <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 tracking-wider">
                          <span className="inline-flex items-center gap-1">
                            <img src="/adjustlogopng.png" alt="Adjust" className="h-3 w-auto" />
                            Rev.
                          </span>
                        </th>
                        <th className="px-3 py-2 text-right text-[11px] font-bold text-blue-600 tracking-wider bg-blue-50/30">
                          <span className="inline-flex items-center gap-1 text-center justify-end w-full">
                            <img src="/GA4logoicon.svg" alt="GA4" className="h-3 w-auto" /> + <img src="/adjustlogopng.png" alt="Adjust" className="h-3 w-auto" /> Rev.
                          </span>
                        </th>
                      </>
                    )}
                    {/* Meta conversion */}
                    <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 tracking-wider">
                      <span className="inline-flex items-center gap-1">
                        <img src="/tiktokv4.svg" alt="TikTok" className="h-3 w-auto" />
                        Conv.
                      </span>
                    </th>
                    {/* GA4 Purchase */}
                    <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 tracking-wider">
                      <span className="inline-flex items-center gap-1">
                        <img src="/GA4logoicon.svg" alt="GA4" className="h-3 w-auto" />
                        Purch.
                      </span>
                    </th>
                    {/* Adjust Purchase */}
                    {showAdjPurchCol && (
                      <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 tracking-wider">
                        <span className="inline-flex items-center gap-1">
                          <img src="/adjustlogopng.png" alt="Adjust" className="h-3 w-auto" />
                          Purch.
                        </span>
                      </th>
                    )}
                    {/* Clicks */}
                    <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 tracking-wider">
                      <span className="inline-flex items-center gap-1">
                        <img src="/tiktokv4.svg" alt="TikTok" className="h-3 w-auto" />
                        Clicks
                      </span>
                    </th>
                    {/* Reach, CPM, Video Views, CPV */}
                    <th className="px-3 py-2 text-right text-[11px] font-medium text-purple-600 tracking-wider bg-purple-50/30">Reach</th>
                    <th className="px-3 py-2 text-right text-[11px] font-medium text-purple-600 tracking-wider bg-purple-50/30">CPM</th>
                    <th className="px-3 py-2 text-right text-[11px] font-medium text-purple-600 tracking-wider bg-purple-50/30">Video Views</th>
                    <th className="px-3 py-2 text-right text-[11px] font-medium text-purple-600 tracking-wider bg-purple-50/30">CPV</th>
                    {/* GA4 Sessions */}
                    <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 tracking-wider">
                      <span className="inline-flex items-center gap-1">
                        <img src="/GA4logoicon.svg" alt="GA4" className="h-3 w-auto" />
                        Sessions
                      </span>
                    </th>
                    {/* Adjust Sessions */}
                    {showAdjSessCol && (
                      <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 tracking-wider">
                        <span className="inline-flex items-center gap-1">
                          <img src="/adjustlogopng.png" alt="Adjust" className="h-3 w-auto" />
                          Sessions
                        </span>
                      </th>
                    )}
                    {/* CPC, CPS, CPI */}
                    <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 tracking-wider">CPC</th>
                    <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 tracking-wider">CPS</th>
                    {showAdjInstCol && (
                      <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 tracking-wider">CPI</th>
                    )}
                    {/* Adjust Installs */}
                    {showAdjInstCol && (
                      <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 tracking-wider">
                        <span className="inline-flex items-center gap-1">
                          <img src="/adjustlogopng.png" alt="Adjust" className="h-3 w-auto" />
                          Installs
                        </span>
                      </th>
                    )}
                    {/* ROAS */}
                    <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 tracking-wider">ROAS</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {computedMetrics?.campaignObjectives?.map((objective) => {
                const objectiveConfig = getObjectiveIconAndColor(objective.id);
                return (
                  <tr key={objective.id} className="hover:bg-gray-50 transition-colors divide-x divide-gray-200">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div 
                          className="p-2 rounded-lg text-white flex-shrink-0"
                          style={{ backgroundColor: objectiveConfig.bgColor }}
                        >
                          {objectiveConfig.icon}
                        </div>
                        <div>
                          <div className="text-xs font-medium text-gray-900">
                            {objective.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium text-gray-900">
                      {formatShortCurrency(objective.totalBudget || 0)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium text-gray-900">
                      {formatShortCurrency(objective.spend || 0)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium text-gray-900">
                      {formatShortCurrency(objective.revenue || objective.purchaseValue || 0)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium text-gray-900">
                      {formatShortCurrency(objective.ga4Revenue || 0)}
                    </td>
                    {showAdjRevCol && (
                      <>
                        <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium text-gray-900">
                            {formatShortCurrency(objective.adjustRevenue || 0)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-bold text-blue-700 bg-blue-50/20">
                            {formatShortCurrency((objective.ga4Revenue || 0) + (objective.adjustRevenue || 0))}
                        </td>
                      </>
                    )}
                    {/* Meta conversion */}
                    <td className="px-3 py-2 whitespace-nowrap text-right text-xs text-gray-900">
                      {formatShortNumber(objective.purchaseConversions || 0)}
                    </td>
                    {/* GA4 Purchase */}
                        <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium text-gray-900">
                      {formatShortNumber(objective.ga4Purchases || 0)}
                        </td>
                    {/* Adjust Purchase */}
                    {showAdjPurchCol && (
                        <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium text-gray-900">
                        {formatShortNumber(objective.adjustPurchases || 0)}
                        </td>
                    )}
                    {/* Clicks */}
                    <td className="px-3 py-2 whitespace-nowrap text-right text-xs text-gray-900">
                      {formatShortNumber(objective.clicks || 0)}
                    </td>
                    {/* Reach, CPM, Video Views, CPV */}
                    <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium text-gray-900">
                      {formatShortNumber(objective.reach || 0)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium text-gray-900">
                      {(objective.reach || 0) > 0 ? formatCurrencyDecimals((objective.spend || 0) / (objective.reach || 0) * 1000) : '₺0.00'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium text-gray-900">
                      {formatShortNumber(objective.view || 0)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium text-gray-900">
                      {(objective.view || 0) > 0 ? formatCurrencyDecimals((objective.spend || 0) / (objective.view || 0)) : '₺0.00'}
                    </td>
                    {/* GA4 Sessions */}
                    <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium text-gray-900">
                      {formatShortNumber(objective.ga4Sessions || 0)}
                    </td>
                    {/* Adjust Sessions */}
                    {showAdjSessCol && (
                      <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium text-gray-900">
                        {formatShortNumber(objective.adjustSessions || 0)}
                      </td>
                    )}
                    {/* CPC */}
                        <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium text-gray-900">
                          {(() => {
                            const clicks = Number(objective.clicks || 0);
                            const spend = Number(objective.spend || 0);
                            return formatCurrencyDecimals(clicks > 0 ? spend / clicks : 0);
                          })()}
                        </td>
                    {/* CPS */}
                        <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium text-gray-900">
                          {(() => {
                            const sessions = Number(objective.ga4Sessions || 0);
                            const spend = Number(objective.spend || 0);
                            return formatCurrencyDecimals(sessions > 0 ? spend / sessions : 0);
                          })()}
                        </td>
                    {/* CPI */}
                    {showAdjInstCol && (
                      <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium text-gray-900">
                        {(() => {
                          const installs = Number(objective.adjustInstalls || 0);
                          const spend = Number(objective.spend || 0);
                          return formatCurrencyDecimals(installs > 0 ? spend / installs : 0);
                        })()}
                      </td>
                    )}
                    {/* Adjust Installs */}
                    {showAdjInstCol && (
                      <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium text-gray-900">
                        {formatShortNumber(objective.adjustInstalls || 0)}
                      </td>
                    )}
                    <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-medium">
                      <span className={`${(objective.roas || 0) >= 2 ? 'text-green-600' : (objective.roas || 0) >= 1 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {(objective.roas || 0).toFixed(2)}x
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr className="divide-x divide-gray-200">
                <td className="px-4 py-2 whitespace-nowrap text-xs font-semibold text-gray-900">
                  TOTAL
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-semibold text-gray-900">
                  {formatShortCurrency(computedMetrics?.campaignObjectives?.reduce((sum, obj) => sum + (obj.totalBudget || 0), 0) || 0)}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-semibold text-gray-900">
                  {formatShortCurrency(computedMetrics?.totalSpend || 0)}
                </td>
                {/* Meta / GA4 / Adjust Revenue totals */}
                <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-semibold text-gray-900">
                  {formatShortCurrency(computedMetrics?.totalPurchaseValue || 0)}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-semibold text-gray-900">
                  {formatShortCurrency(computedMetrics?.totalGa4Revenue || 0)}
                </td>
                {showAdjRevCol && (
                  <>
                    <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-semibold text-gray-900">
                        {formatShortCurrency(computedMetrics?.totalAdjustRevenue || 0)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-bold text-blue-700 bg-blue-50/30">
                        {formatShortCurrency((computedMetrics?.totalGa4Revenue || 0) + (computedMetrics?.totalAdjustRevenue || 0))}
                    </td>
                  </>
                )}
                {/* Meta conv, GA4 Purch, Adjust Purch totals */}
                    <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-semibold text-gray-900">
                  {formatShortNumber(computedMetrics?.totalPurchaseConversions || 0)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-semibold text-gray-900">
                  {formatShortNumber(computedMetrics?.campaignObjectives?.reduce((sum, obj) => sum + (obj.ga4Purchases || 0), 0) || 0)}
                    </td>
                {showAdjPurchCol && (
                <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-semibold text-gray-900">
                    {formatShortNumber(computedMetrics?.totalAdjustPurchases || 0)}
                </td>
                )}
                {/* Meta Clicks total */}
                <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-semibold text-gray-900">
                  {formatShortNumber(computedMetrics?.totalClicks || 0)}
                </td>
                {/* Reach, CPM, Video Views, CPV totals */}
                <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-semibold text-gray-900">
                  {formatShortNumber(computedMetrics?.totalReach || 0)}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-semibold text-gray-900">
                  {(() => {
                    const reach = Number(computedMetrics?.totalReach || 0);
                    const spend = Number(computedMetrics?.totalSpend || 0);
                    return reach > 0 ? formatCurrencyDecimals(spend / reach * 1000) : '₺0.00';
                  })()}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-semibold text-gray-900">
                  {formatShortNumber(computedMetrics?.totalView || 0)}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-semibold text-gray-900">
                  {(() => {
                    const view = Number(computedMetrics?.totalView || 0);
                    const spend = Number(computedMetrics?.totalSpend || 0);
                    return view > 0 ? formatCurrencyDecimals(spend / view) : '₺0.00';
                  })()}
                </td>
                {/* GA4 Sessions, Adjust Sessions totals */}
                <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-semibold text-gray-900">
                  {formatShortNumber(computedMetrics?.campaignObjectives?.reduce((sum, obj) => sum + (obj.ga4Sessions || 0), 0) || 0)}
                </td>
                {showAdjSessCol && (
                  <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-semibold text-gray-900">
                    {formatShortNumber(computedMetrics?.totalAdjustSessions || 0)}
                  </td>
                )}
                {/* CPC, CPS totals */}
                <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-semibold text-gray-900">
                  {(() => {
                    const clicks = Number(computedMetrics?.totalClicks || 0);
                    const spend = Number(computedMetrics?.totalSpend || 0);
                    return formatCurrencyDecimals(clicks > 0 ? spend / clicks : 0);
                  })()}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-semibold text-gray-900">
                  {(() => {
                    const sessions = Number(computedMetrics?.totalGa4Sessions || 0);
                    const spend = Number(computedMetrics?.totalSpend || 0);
                    return formatCurrencyDecimals(sessions > 0 ? spend / sessions : 0);
                  })()}
                </td>
                {/* CPI total */}
                {showAdjInstCol && (
                  <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-semibold text-gray-900">
                    {(() => {
                      const installs = Number(computedMetrics?.totalAdjustInstalls || 0);
                      const spend = Number(computedMetrics?.totalSpend || 0);
                      return formatCurrencyDecimals(installs > 0 ? spend / installs : 0);
                    })()}
                  </td>
                )}
                {/* Adjust Installs total */}
                {showAdjInstCol && (
                <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-semibold text-gray-900">
                    {formatShortNumber(computedMetrics?.totalAdjustInstalls || 0)}
                </td>
                )}
                <td className="px-3 py-2 whitespace-nowrap text-right text-xs font-semibold text-gray-900">
                  <span className={`${(computedMetrics?.roas || 0) >= 2 ? 'text-green-600' : (computedMetrics?.roas || 0) >= 1 ? 'text-yellow-600' : 'text-red-600'}`}>
                    {(computedMetrics?.roas || 0).toFixed(2)}x
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Objective Cards */}
      <div className="space-y-4">
        <h3 className="font-medium text-gray-800">Campaign Objective Details</h3>
        
        <div className="grid grid-cols-1 gap-3">
          {loading ? (
            // Loading placeholder
            Array(3).fill(0).map((_, index) => (
              <Card key={index} className="rounded-xl overflow-hidden">
                <div className="p-4 border-b bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gray-200 animate-pulse"></div>
                    <div>
                      <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
                      <div className="h-3 w-24 bg-gray-200 rounded mt-1 animate-pulse"></div>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-6 gap-3">
                    {Array(6).fill(0).map((_, i) => (
                      <div key={i} className="text-center">
                        <div className="h-3 w-12 mx-auto bg-gray-200 rounded animate-pulse mb-2"></div>
                        <div className="h-5 w-16 mx-auto bg-gray-200 rounded animate-pulse"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            ))
          ) : !computedMetrics.campaignObjectives || computedMetrics.campaignObjectives.length === 0 ? (
            <div className="p-8 text-center bg-gray-50 rounded-xl">
              <p className="text-gray-500">No campaign data available</p>
            </div>
          ) : (
            (computedMetrics.campaignObjectives || []).map(objective => {
              const { icon, color, bgColor } = getObjectiveIconAndColor(objective.id);
              return (
                <Card key={objective.id} className="rounded-xl overflow-hidden shadow-md">
                  <div className={`p-4 border-b bg-${color}-50`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center`} style={{ backgroundColor: `${bgColor}20` }}>
                          <div className="text-gray-800" style={{ color: bgColor }}>
                            {icon}
                          </div>
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-800">{objective.name}</h3>
                          <p className="text-xs text-gray-500">{objective.campaigns} active campaigns</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                          {(objectiveFilterTags[objective.id] || []).map((tag, ti) => (
                            <span key={ti} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[11px] font-medium">
                              {tag}
                              <button onClick={() => setObjectiveFilterTags(prev => ({
                                ...prev,
                                [objective.id]: (prev[objective.id] || []).filter((_, idx) => idx !== ti)
                              }))} className="hover:text-blue-900">
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                            <input
                              type="text"
                              placeholder={`Filter...`}
                              value={objectiveFilterInputs[objective.id] || ''}
                              onChange={(e) => setObjectiveFilterInputs(prev => ({ ...prev, [objective.id]: e.target.value }))}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && (objectiveFilterInputs[objective.id] || '').trim()) {
                                  e.preventDefault();
                                  const val = (objectiveFilterInputs[objective.id] || '').trim();
                                  setObjectiveFilterTags(prev => ({
                                    ...prev,
                                    [objective.id]: [...(prev[objective.id] || []), val]
                                  }));
                                  setObjectiveFilterInputs(prev => ({ ...prev, [objective.id]: '' }));
                                }
                                if (e.key === 'Backspace' && !(objectiveFilterInputs[objective.id] || '')) {
                                  setObjectiveFilterTags(prev => ({
                                    ...prev,
                                    [objective.id]: (prev[objective.id] || []).slice(0, -1)
                                  }));
                                }
                              }}
                              className="pl-7 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white/80 focus:outline-none focus:ring-1 focus:ring-blue-300 focus:border-blue-300 w-40"
                            />
                          </div>
                        </div>
                    </div>
                  </div>
                  
                  <div className="p-4">
                    {objective.campaignList && objective.campaignList.length > 0 && (() => {
                      const tags = (objectiveFilterTags[objective.id] || []).map(t => t.toLowerCase());
                      const filteredCampaignList = tags.length > 0
                        ? objective.campaignList.filter(c => {
                            const name = (c.name || '').toLowerCase();
                            return tags.some(t => name.includes(t));
                          })
                        : objective.campaignList;
                      if (filteredCampaignList.length === 0) return (
                        <div className="p-4 text-center text-gray-400 text-sm">No campaigns matching filter</div>
                      );
                      return (
                      <div>
        <div className="overflow-x-auto w-full">
          <table className="min-w-[1400px] w-full border border-gray-200 shadow-lg rounded-lg">
                            <thead className="bg-gray-50">
                              <tr className="divide-x divide-gray-200">
                                <th className="w-10 px-3 py-2 text-left text-[11px] font-medium text-gray-500 tracking-wider border-b-2 border-gray-200"></th>
                                <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 tracking-wider border-b-2 border-gray-200">Campaign</th>
                                <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 tracking-wider border-b-2 border-gray-200">Budget</th>
                                <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 tracking-wider border-b-2 border-gray-200">Spend</th>
                                <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 tracking-wider border-b-2 border-gray-200">
                                  <span className="inline-flex items-center gap-1">
                                    <img src="/tiktokv4.svg" alt="TikTok" className="h-3 w-auto" /> Rev.
                                  </span>
                                </th>
                                <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 tracking-wider border-b-2 border-gray-200">
                                  <span className="inline-flex items-center gap-1">
                                    <img src="/GA4logoicon.svg" alt="GA4" className="h-3 w-auto" /> Rev.
                                  </span>
                                </th>
                                {showAdjRevCol && (
                                  <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 tracking-wider border-b-2 border-gray-200">
                                    <span className="inline-flex items-center gap-1">
                                      <img src="/adjustlogopng.png" alt="Adjust" className="h-3 w-auto" /> Rev.
                                    </span>
                                  </th>
                                )}
                                {showAdjRevCol && (
                                  <th className="px-3 py-2 text-left text-[11px] font-bold text-blue-600 tracking-wider border-b-2 border-gray-200 bg-blue-50/30">
                                    <span className="inline-flex items-center gap-1 text-center justify-end w-full">
                                      <img src="/GA4logoicon.svg" alt="GA4" className="h-3 w-auto" /> + <img src="/adjustlogopng.png" alt="Adjust" className="h-3 w-auto" /> Rev.
                                    </span>
                                  </th>
                                )}
                                <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 tracking-wider border-b-2 border-gray-200">
                                  <span className="inline-flex items-center gap-1">
                                    <img src="/tiktokv4.svg" alt="TikTok" className="h-3 w-auto" /> Conv.
                                  </span>
                                </th>
                                <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 tracking-wider border-b-2 border-gray-200">
                                  <span className="inline-flex items-center gap-1">
                                    <img src="/GA4logoicon.svg" alt="GA4" className="h-3 w-auto" /> Purch.
                                  </span>
                                </th>
                                {showAdjPurchCol && (
                                  <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 tracking-wider border-b-2 border-gray-200">
                                    <span className="inline-flex items-center gap-1">
                                      <img src="/adjustlogopng.png" alt="Adjust" className="h-3 w-auto" /> Purch.
                                    </span>
                                  </th>
                                )}
                                <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 tracking-wider border-b-2 border-gray-200">
                                  <span className="inline-flex items-center gap-1">
                                    <img src="/tiktokv4.svg" alt="TikTok" className="h-3 w-auto" /> Clicks
                                  </span>
                                </th>
                                {['awareness', 'outcome_awareness', 'engagement', 'brand_awareness', 'outcome_engagement'].includes((objective.id || '').toLowerCase()) && (
                                  <>
                                    <th className="px-3 py-2 text-left text-[11px] font-medium text-purple-600 tracking-wider border-b-2 border-gray-200 bg-purple-50/30">Reach</th>
                                    <th className="px-3 py-2 text-left text-[11px] font-medium text-purple-600 tracking-wider border-b-2 border-gray-200 bg-purple-50/30">CPM</th>
                                    <th className="px-3 py-2 text-left text-[11px] font-medium text-purple-600 tracking-wider border-b-2 border-gray-200 bg-purple-50/30">Video Views</th>
                                    <th className="px-3 py-2 text-left text-[11px] font-medium text-purple-600 tracking-wider border-b-2 border-gray-200 bg-purple-50/30">CPV</th>
                                  </>
                                )}
                                <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 tracking-wider border-b-2 border-gray-200">
                                  <span className="inline-flex items-center gap-1">
                                    <img src="/GA4logoicon.svg" alt="GA4" className="h-3 w-auto" /> Sessions
                                  </span>
                                </th>
                                {showAdjSessCol && (
                                  <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 tracking-wider border-b-2 border-gray-200">
                                    <span className="inline-flex items-center gap-1">
                                      <img src="/adjustlogopng.png" alt="Adjust" className="h-3 w-auto" /> Sessions
                                    </span>
                                  </th>
                                )}
                                <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 tracking-wider border-b-2 border-gray-200">CPC</th>
                                <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 tracking-wider border-b-2 border-gray-200">CPS</th>
                                {showAdjInstCol && (
                                  <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 tracking-wider border-b-2 border-gray-200">CPI</th>
                                )}
                                {showAdjInstCol && (
                                  <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 tracking-wider border-b-2 border-gray-200">
                                    <span className="inline-flex items-center gap-1">
                                      <img src="/adjustlogopng.png" alt="Adjust" className="h-3 w-auto" /> Installs
                                    </span>
                                  </th>
                                )}
                                <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 tracking-wider border-b-2 border-gray-200">ROAS</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                              {filteredCampaignList.map(campaign => {
                                const liveEntry = liveDataMap[String(campaign.id)];
                                const liveStatus = liveEntry?.status || null;
                                const isActive = liveStatus === 'ACTIVE' || liveStatus === 'ENABLED';
                                const isPaused = liveStatus === 'PAUSED';
                                const hasLiveStatus = liveStatus != null;
                                const liveBudget = liveEntry?.budget != null ? liveEntry.budget : null;
                                const liveBudgetType = liveEntry?.budget_type || null;
                                const isLifetimeBudget = liveBudgetType === 'lifetime';
                                return (
                                <React.Fragment key={campaign.id}>
                                  {/* Campaign row */}
                                  <tr className="hover:bg-gray-50 border-b border-gray-200 group/row">
                                    <td className="px-2 py-2.5 border-r border-gray-100">
                                      <div className="flex items-center gap-1 justify-center relative">
                                        {/* Live status dot (clickable) */}
                                        <div className="relative flex-shrink-0">
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setOpenStatusDropdown(openStatusDropdown === campaign.id ? null : campaign.id);
                                            }}
                                            disabled={updatingId === campaign.id || !hasLiveStatus}
                                            className="flex items-center gap-0.5 p-0.5 rounded hover:bg-gray-100 transition-colors focus:outline-none"
                                            title={!hasLiveStatus ? 'Live status unavailable' : isActive ? 'Active — click to change' : 'Paused — click to change'}
                                          >
                                            {updatingId === campaign.id ? (
                                              <Loader2 className="w-2.5 h-2.5 animate-spin text-gray-500" />
                                            ) : (
                                              <div className={`w-2.5 h-2.5 rounded-full ${
                                                !hasLiveStatus ? 'bg-gray-300' :
                                                isActive ? 'bg-green-500 shadow-[0_0_0_3px_rgba(34,197,94,0.15)]' :
                                                'bg-gray-400 shadow-[0_0_0_3px_rgba(156,163,175,0.15)]'
                                              }`} />
                                            )}
                                            {hasLiveStatus && (
                                              <ChevronDown className="w-3 h-3 text-gray-400 opacity-0 group-hover/row:opacity-100 transition-opacity" />
                                            )}
                                          </button>
                                          {/* Status dropdown */}
                                          {openStatusDropdown === campaign.id && (
                                            <>
                                              <div className="fixed inset-0 z-10 cursor-default" onClick={() => setOpenStatusDropdown(null)} />
                                              <div className="absolute top-full left-0 mt-1 w-40 bg-white rounded-xl shadow-xl border border-gray-100 z-20 py-1 overflow-hidden">
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpenStatusDropdown(null);
                                                    setConfirmDialog({
                                                      title: 'Enable Campaign',
                                                      message: `Enable campaign "${campaign.name}"?`,
                                                      variant: 'enable',
                                                      onConfirm: () => {
                                                        updateEntity(campaign.id, 'status', 'ACTIVE', liveStatus || 'PAUSED', campaign.name, 'campaign');
                                                        setConfirmDialog(null);
                                                      }
                                                    });
                                                  }}
                                                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-3 transition-colors ${isActive ? 'text-green-600 bg-green-50/50' : 'text-gray-700'}`}
                                                >
                                                  <div className="w-2 h-2 rounded-full bg-green-500" />
                                                  <span>Enable</span>
                                                  {isActive && <Check className="w-3.5 h-3.5 ml-auto" />}
                                                </button>
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpenStatusDropdown(null);
                                                    setConfirmDialog({
                                                      title: 'Pause Campaign',
                                                      message: `Pause campaign "${campaign.name}"?`,
                                                      variant: 'pause',
                                                      onConfirm: () => {
                                                        updateEntity(campaign.id, 'status', 'PAUSED', liveStatus || 'ACTIVE', campaign.name, 'campaign');
                                                        setConfirmDialog(null);
                                                      }
                                                    });
                                                  }}
                                                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-3 transition-colors ${isPaused ? 'text-gray-900 bg-gray-50' : 'text-gray-700'}`}
                                                >
                                                  <div className="w-2 h-2 rounded-full bg-gray-400" />
                                                  <span>Pause</span>
                                                  {isPaused && <Check className="w-3.5 h-3.5 ml-auto" />}
                                                </button>
                                              </div>
                                            </>
                                          )}
                                        </div>
                                        {campaign.adSets && campaign.adSets.length > 0 && (
                                          <button
                                            onClick={() => setExpandedCampaigns(prev => ({
                                              ...prev,
                                              [campaign.id]: !prev[campaign.id]
                                            }))}
                                            className="p-0.5 rounded hover:bg-gray-200"
                                          >
                                            {expandedCampaigns[campaign.id] ? (
                                              <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
                                            ) : (
                                              <ChevronRight className="w-3.5 h-3.5 text-gray-500" />
                                            )}
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                    {/* Campaign name */}
                                    <td className="px-3 py-2.5 border-r border-gray-100">
                                      <span className="text-xs font-medium text-gray-800">{campaign.name}</span>
                                    </td>
                                    {/* Budget column: live budget with inline edit */}
                                    <td className="px-3 py-2.5 border-r border-gray-100" onClick={e => e.stopPropagation()}>
                                      {(() => {
                                        if (isLifetimeBudget) {
                                          return (
                                            <span className="text-xs text-gray-500">
                                              {liveBudget != null ? `₺${liveBudget.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}` : formatShortCurrency(campaign.budgetAmount || 0)}
                                              <span className="ml-1 text-[10px] text-gray-400">(Lifetime)</span>
                                            </span>
                                          );
                                        }
                                        if (editBudget?.id === campaign.id) {
                                          return (
                                            <div className="flex items-center gap-1">
                                              <input
                                                type="number"
                                                value={editBudget.value}
                                                onChange={e => setEditBudget({ ...editBudget, value: e.target.value })}
                                                onKeyDown={e => {
                                                  if (e.key === 'Enter') handleSaveBudget(campaign.id);
                                                  if (e.key === 'Escape') setEditBudget(null);
                                                }}
                                                className="w-20 px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                                                autoFocus
                                              />
                                              <button onClick={() => handleSaveBudget(campaign.id)} className="p-0.5 text-green-600 hover:bg-green-50 rounded">
                                                <Check className="w-3.5 h-3.5" />
                                              </button>
                                              <button onClick={() => setEditBudget(null)} className="p-0.5 text-gray-400 hover:bg-gray-100 rounded">
                                                <X className="w-3.5 h-3.5" />
                                              </button>
                                            </div>
                                          );
                                        }
                                        const displayBudget = liveBudget != null ? liveBudget : (campaign.budgetAmount || 0);
                                        return (
                                          <div className="flex items-center gap-1 group/budget">
                                            <span className="text-xs text-gray-600">
                                              {`₺${displayBudget.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`}
                                            </span>
                                            {liveBudget != null && !isLifetimeBudget && (
                                              <button
                                                onClick={() => setEditBudget({ id: campaign.id, value: liveBudget || 0, originalValue: liveBudget || 0, name: campaign.name })}
                                                disabled={updatingId === campaign.id}
                                                className="opacity-0 group-hover/budget:opacity-100 p-0.5 text-gray-400 hover:text-blue-600 transition-opacity"
                                              >
                                                <Edit2 className="w-3 h-3" />
                                              </button>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </td>
                                    <td className="px-3 py-2.5 text-xs text-gray-600 border-r border-gray-100">{formatShortCurrency(campaign.spend)}</td>
                                    {/* Meta, GA4, Adjust Revenue */}
                                    <td className="px-3 py-2.5 text-xs text-gray-600 border-r border-gray-100">{formatShortCurrency(campaign.purchaseValue || 0)}</td>
                                    <td className="px-3 py-2.5 text-xs text-gray-600 border-r border-gray-100">{formatShortCurrency(campaign.ga4Revenue || 0)}</td>
                                    {showAdjRevCol && (
                                      <>
                                        <td className="px-3 py-2.5 text-xs text-gray-600 border-r border-gray-100">{formatShortCurrency(campaign.adjustRevenue || 0)}</td>
                                        <td className="px-3 py-2.5 text-xs font-bold text-blue-700 border-r border-gray-100 bg-blue-50/20">{formatShortCurrency((campaign.ga4Revenue || 0) + (campaign.adjustRevenue || 0))}</td>
                                      </>
                                    )}
                                    {/* Meta conv, GA4 Purch, Adjust Purch */}
                                    <td className="px-3 py-2.5 text-xs text-gray-600 border-r border-gray-100">{formatShortNumber(campaign.purchaseConversions || 0)}</td>
                                    <td className="px-3 py-2.5 text-xs text-gray-600 border-r border-gray-100">{formatShortNumber(campaign.ga4Purchases || 0)}</td>
                                    {showAdjPurchCol && (
                                      <td className="px-3 py-2.5 text-xs text-gray-600 border-r border-gray-100">{formatShortNumber(campaign.adjustPurchases || 0)}</td>
                                    )}
                                    {/* Clicks */}
                                    <td className="px-3 py-2.5 text-xs text-gray-600 border-r border-gray-100">{formatShortNumber(campaign.clicks)}</td>
                                    {/* Reach, CPM, Video Views, CPV - Awareness/Engagement */}
                                    {['awareness', 'outcome_awareness', 'engagement', 'brand_awareness', 'outcome_engagement'].includes((objective.id || '').toLowerCase()) && (
                                      <>
                                        <td className="px-3 py-2.5 text-xs text-gray-600 border-r border-gray-100">{formatShortNumber(campaign.reach || 0)}</td>
                                        <td className="px-3 py-2.5 text-xs text-gray-600 border-r border-gray-100">{(campaign.reach || 0) > 0 ? formatCurrencyDecimals((campaign.spend || 0) / (campaign.reach || 0) * 1000) : '₺0.00'}</td>
                                        <td className="px-3 py-2.5 text-xs text-gray-600 border-r border-gray-100">{formatShortNumber(campaign.view || 0)}</td>
                                        <td className="px-3 py-2.5 text-xs text-gray-600 border-r border-gray-100">{(campaign.view || 0) > 0 ? formatCurrencyDecimals((campaign.spend || 0) / (campaign.view || 0)) : '₺0.00'}</td>
                                      </>
                                    )}
                                    {/* GA4 Sessions, Adjust Sessions */}
                                    <td className="px-3 py-2.5 text-xs text-gray-600 border-r border-gray-100">{formatShortNumber(campaign.ga4Sessions || 0)}</td>
                                    {showAdjSessCol && (
                                      <td className="px-3 py-2.5 text-xs text-gray-600 border-r border-gray-100">{formatShortNumber(campaign.adjustSessions || 0)}</td>
                                    )}
                                    {/* CPC, CPS, CPI, ROAS */}
                                    <td className="px-3 py-2.5 text-xs text-gray-600 border-r border-gray-100">{formatCurrencyDecimals(campaign.cpc)}</td>
                                    <td className="px-3 py-2.5 text-xs text-gray-600 border-r border-gray-100">{(campaign.ga4Sessions || 0) > 0 ? formatCurrencyDecimals((campaign.spend || 0) / (campaign.ga4Sessions || 0)) : '₺0.00'}</td>
                                    {showAdjInstCol && (
                                      <td className="px-3 py-2.5 text-xs text-gray-600 border-r border-gray-100">
                                        {(() => {
                                          const installs = Number(campaign.adjustInstalls || 0);
                                          const spend = Number(campaign.spend || 0);
                                          return formatCurrencyDecimals(installs > 0 ? spend / installs : 0);
                                        })()}
                                      </td>
                                    )}
                                    {showAdjInstCol && (
                                      <td className="px-3 py-2.5 text-xs text-gray-600 border-r border-gray-100">{formatShortNumber(campaign.adjustInstalls || 0)}</td>
                                    )}
                                    <td className="px-3 py-2.5 text-xs font-medium border-r border-gray-100">
                                      <span className={`${(campaign.roas || 0) >= 2 ? 'text-green-600' : (campaign.roas || 0) >= 1 ? 'text-yellow-600' : 'text-red-600'}`}>
                                        {(campaign.roas || 0).toFixed(2)}x
                                      </span>
                                    </td>
                                  </tr>
                                  
                                  {/* Ad Sets section */}
                                  {expandedCampaigns[campaign.id] && campaign.adSets && campaign.adSets.length > 0 && 
                                    campaign.adSets.map(adSet => (
                                      <React.Fragment key={adSet.id}>
                                        {/* Ad Set row */}
                                        <tr className="bg-gray-50 border-b border-gray-200">
                                          <td className="px-2 py-2 border-r border-gray-100">
                                            {adSet.ads && adSet.ads.length > 0 && (
                                              <button 
                                                onClick={() => setExpandedAdSets(prev => ({
                                                  ...prev, 
                                                  [adSet.id]: !prev[adSet.id]
                                                }))}
                                                className="p-1 ml-4 rounded hover:bg-gray-200"
                                              >
                                                {expandedAdSets[adSet.id] ? (
                                                  <ChevronDown className="w-3 h-3 text-gray-500" />
                                                ) : (
                                                  <ChevronRight className="w-3 h-3 text-gray-500" />
                                                )}
                                              </button>
                                            )}
                                          </td>
                                          <td className="px-3 py-2 text-xs text-gray-600 pl-8 border-r border-gray-100">
                                            <div className="flex items-center">
                                              <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mr-2"></div>
                                              {adSet.name}
                                            </div>
                                          </td>
                                          <td className="px-3 py-2 border-r border-gray-100"></td>
                                          <td className="px-3 py-2 text-xs text-gray-600 border-r border-gray-100">{formatCurrency(adSet.spend)}</td>
                                          <td className="px-3 py-2 text-xs text-gray-600 border-r border-gray-100">{formatCurrency(adSet.purchaseValue || 0)}</td>
                                          <td className="px-3 py-2 text-xs text-gray-600 border-r border-gray-100">{formatNumber(adSet.impressions)}</td>
                                          <td className="px-3 py-2 text-xs text-gray-600 border-r border-gray-100">{formatNumber(adSet.clicks)}</td>
                                          <td className="px-3 py-2 text-xs text-gray-600 border-r border-gray-100">{adSet.ctr}%</td>
                                          <td className="px-3 py-2 text-xs text-gray-600 border-r border-gray-100">{formatCurrencyDecimals(adSet.cpc)}</td>
                                          <td className="px-3 py-2 text-xs text-gray-600 border-r border-gray-100">{adSet.purchaseConversions}</td>
                                          <td className="px-3 py-2">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                              adSet.roas >= 3 ? 'bg-green-100 text-green-700' : 
                                              adSet.roas >= 2 ? 'bg-blue-100 text-blue-700' : 
                                              adSet.roas >= 1 ? 'bg-yellow-100 text-yellow-700' :
                                              'bg-orange-100 text-orange-700'
                                            }`}>
                                              {adSet.roas.toFixed(1)}x
                                            </span>
                                          </td>
                                        </tr>
                                        
                                        {/* Ads section */}
                                        {expandedAdSets[adSet.id] && adSet.ads && adSet.ads.length > 0 && 
                                          adSet.ads.map(ad => (
                                            <tr key={ad.id} className="bg-gray-100 border-b border-gray-200">
                                              <td className="px-2 py-2 border-r border-gray-100"></td>
                                              <td className="px-3 py-2 text-xs text-gray-600 pl-14 border-r border-gray-100">
                                                <div className="flex items-center">
                                                  <div className="w-1 h-1 rounded-full bg-gray-400 mr-2"></div>
                                                  {ad.name}
                                                </div>
                                              </td>
                                              <td className="px-3 py-2 border-r border-gray-100"></td>
                                              <td className="px-3 py-2 text-xs text-gray-600 border-r border-gray-100">{formatCurrency(ad.spend)}</td>
                                              <td className="px-3 py-2 text-xs text-gray-600 border-r border-gray-100">{formatCurrency(ad.purchaseValue || 0)}</td>
                                              <td className="px-3 py-2 text-xs text-gray-600 border-r border-gray-100">{formatNumber(ad.impressions)}</td>
                                              <td className="px-3 py-2 text-xs text-gray-600 border-r border-gray-100">{formatNumber(ad.clicks)}</td>
                                              <td className="px-3 py-2 text-xs text-gray-600 border-r border-gray-100">{ad.ctr}%</td>
                                              <td className="px-3 py-2 text-xs text-gray-600 border-r border-gray-100">{formatCurrencyDecimals(ad.cpc)}</td>
                                              <td className="px-3 py-2 text-xs text-gray-600 border-r border-gray-100">{ad.purchaseConversions}</td>
                                              <td className="px-3 py-2 text-xs text-gray-600 border-r border-gray-100">{(ad.ga4Sessions || 0) > 0 ? formatCurrencyDecimals((ad.spend || 0) / (ad.ga4Sessions || 0)) : '₺0.00'}</td>
                                              <td className="px-3 py-2">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                  ad.roas >= 3 ? 'bg-green-100 text-green-700' : 
                                                  ad.roas >= 2 ? 'bg-blue-100 text-blue-700' : 
                                                  ad.roas >= 1 ? 'bg-yellow-100 text-yellow-700' :
                                                  'bg-orange-100 text-orange-700'
                                                }`}>
                                                  {ad.roas.toFixed(1)}x
                                                </span>
                                              </td>
                                            </tr>
                                          ))
                                        }
                                      </React.Fragment>
                                    ))
                                  }
                                </React.Fragment>
                                );
                              })}
                            </tbody>
                            <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                              {(() => {
                                const campaigns = filteredCampaignList;
                                const totBudget = campaigns.reduce((s, c) => {
                                  const le = liveDataMap[String(c.id)];
                                  return s + (le?.budget != null ? le.budget : (c.budgetAmount || 0));
                                }, 0);
                                const totSpend = campaigns.reduce((s, c) => s + Number(c.spend || 0), 0);
                                const totMetaRev = campaigns.reduce((s, c) => s + Number(c.purchaseValue || 0), 0);
                                const totGa4Rev = campaigns.reduce((s, c) => s + Number(c.ga4Revenue || 0), 0);
                                const totAdjRev = campaigns.reduce((s, c) => s + Number(c.adjustRevenue || 0), 0);
                                const totMetaConv = campaigns.reduce((s, c) => s + Number(c.purchaseConversions || 0), 0);
                                const totGa4Purch = campaigns.reduce((s, c) => s + Number(c.ga4Purchases || 0), 0);
                                const totAdjPurch = campaigns.reduce((s, c) => s + Number(c.adjustPurchases || 0), 0);
                                const totClicks = campaigns.reduce((s, c) => s + Number(c.clicks || 0), 0);
                                const totReach = campaigns.reduce((s, c) => s + Number(c.reach || 0), 0);
                                const totView = campaigns.reduce((s, c) => s + Number(c.view || 0), 0);
                                const totGa4Sess = campaigns.reduce((s, c) => s + Number(c.ga4Sessions || 0), 0);
                                const totAdjSess = campaigns.reduce((s, c) => s + Number(c.adjustSessions || 0), 0);
                                const totAdjInst = campaigns.reduce((s, c) => s + Number(c.adjustInstalls || 0), 0);
                                const isAwarenessObj = ['awareness', 'outcome_awareness', 'engagement', 'brand_awareness', 'outcome_engagement'].includes((objective.id || '').toLowerCase());
                                const totalRoas = totSpend > 0 ? totGa4Rev / totSpend : 0;
                                return (
                                  <tr className="divide-x divide-gray-200">
                                    <td className="px-2 py-2.5 border-r border-gray-200"></td>
                                    <td className="px-3 py-2.5 text-xs font-bold text-gray-900 border-r border-gray-200">TOTAL</td>
                                    <td className="px-3 py-2.5 text-xs font-bold text-gray-900 border-r border-gray-200">{formatShortCurrency(totBudget)}</td>
                                    <td className="px-3 py-2.5 text-xs font-bold text-gray-900 border-r border-gray-200">{formatShortCurrency(totSpend)}</td>
                                    <td className="px-3 py-2.5 text-xs font-bold text-gray-900 border-r border-gray-200">{formatShortCurrency(totMetaRev)}</td>
                                    <td className="px-3 py-2.5 text-xs font-bold text-gray-900 border-r border-gray-200">{formatShortCurrency(totGa4Rev)}</td>
                                    {showAdjRevCol && (
                                      <>
                                        <td className="px-3 py-2.5 text-xs font-bold text-gray-900 border-r border-gray-200">{formatShortCurrency(totAdjRev)}</td>
                                        <td className="px-3 py-2.5 text-xs font-bold text-blue-700 border-r border-gray-200 bg-blue-50/30">{formatShortCurrency(totGa4Rev + totAdjRev)}</td>
                                      </>
                                    )}
                                    <td className="px-3 py-2.5 text-xs font-bold text-gray-900 border-r border-gray-200">{formatShortNumber(totMetaConv)}</td>
                                    <td className="px-3 py-2.5 text-xs font-bold text-gray-900 border-r border-gray-200">{formatShortNumber(totGa4Purch)}</td>
                                    {showAdjPurchCol && (
                                      <td className="px-3 py-2.5 text-xs font-bold text-gray-900 border-r border-gray-200">{formatShortNumber(totAdjPurch)}</td>
                                    )}
                                    <td className="px-3 py-2.5 text-xs font-bold text-gray-900 border-r border-gray-200">{formatShortNumber(totClicks)}</td>
                                    {isAwarenessObj && (
                                      <>
                                        <td className="px-3 py-2.5 text-xs font-bold text-gray-900 border-r border-gray-200">{formatShortNumber(totReach)}</td>
                                        <td className="px-3 py-2.5 text-xs font-bold text-gray-900 border-r border-gray-200">{totReach > 0 ? formatCurrencyDecimals(totSpend / totReach * 1000) : '₺0.00'}</td>
                                        <td className="px-3 py-2.5 text-xs font-bold text-gray-900 border-r border-gray-200">{formatShortNumber(totView)}</td>
                                        <td className="px-3 py-2.5 text-xs font-bold text-gray-900 border-r border-gray-200">{totView > 0 ? formatCurrencyDecimals(totSpend / totView) : '₺0.00'}</td>
                                      </>
                                    )}
                                    <td className="px-3 py-2.5 text-xs font-bold text-gray-900 border-r border-gray-200">{formatShortNumber(totGa4Sess)}</td>
                                    {showAdjSessCol && (
                                      <td className="px-3 py-2.5 text-xs font-bold text-gray-900 border-r border-gray-200">{formatShortNumber(totAdjSess)}</td>
                                    )}
                                    <td className="px-3 py-2.5 text-xs font-bold text-gray-900 border-r border-gray-200">{formatCurrencyDecimals(totClicks > 0 ? totSpend / totClicks : 0)}</td>
                                    <td className="px-3 py-2.5 text-xs font-bold text-gray-900 border-r border-gray-200">{formatCurrencyDecimals(totGa4Sess > 0 ? totSpend / totGa4Sess : 0)}</td>
                                    {showAdjInstCol && (
                                      <td className="px-3 py-2.5 text-xs font-bold text-gray-900 border-r border-gray-200">{formatCurrencyDecimals(totAdjInst > 0 ? totSpend / totAdjInst : 0)}</td>
                                    )}
                                    {showAdjInstCol && (
                                      <td className="px-3 py-2.5 text-xs font-bold text-gray-900 border-r border-gray-200">{formatShortNumber(totAdjInst)}</td>
                                    )}
                                    <td className="px-3 py-2.5 text-xs font-bold">
                                      <span className={`${totalRoas >= 2 ? 'text-green-600' : totalRoas >= 1 ? 'text-yellow-600' : 'text-red-600'}`}>
                                        {totalRoas.toFixed(2)}x
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })()}
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    );
                    })()}
                  </div>
                </Card>
              );
            })
          )}
        </div>
      </div>

      {/* Campaign Performance Trends (Monthly) - Pivot */}
      <Card className="rounded-2xl overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Campaign Performance Trends (Monthly)</h2>
          <div className="flex items-center gap-3 justify-between mt-2">
            <p className="text-sm text-gray-500">Monthly breakdown by campaign objective</p>
            {(() => {
              const hasAdjRevenueTrend =
                (monthlyPivot.objectives || []).some(o => (o.totals || []).some(t => (t.adjustRevenue || 0) > 0)) ||
                (dailyPivot10.objectives || []).some(o => (o.totals || []).some(t => (t.adjustRevenue || 0) > 0));
              const hasAdjSessionsTrend =
                (monthlyPivot.objectives || []).some(o => (o.totals || []).some(t => (t.adjustSessions || 0) > 0)) ||
                (dailyPivot10.objectives || []).some(o => (o.totals || []).some(t => (t.adjustSessions || 0) > 0));
              const hasAdjPurchasesTrend =
                (monthlyPivot.objectives || []).some(o => (o.totals || []).some(t => (t.adjustPurchases || 0) > 0)) ||
                (dailyPivot10.objectives || []).some(o => (o.totals || []).some(t => (t.adjustPurchases || 0) > 0));
              const hasAdjInstallsTrend =
                (monthlyPivot.objectives || []).some(o => (o.totals || []).some(t => (t.adjustInstalls || 0) > 0)) ||
                (dailyPivot10.objectives || []).some(o => (o.totals || []).some(t => (t.adjustInstalls || 0) > 0));
              const metricOptions = [
                { key: 'cost', label: 'Cost' },
                { key: 'revenue', label: 'Revenue' },
                ...(hasAdjRevenueTrend ? [{ key: 'adjustRevenue', label: 'Adj. Rev.' }] : []),
                { key: 'costRevenue', label: 'Cost+Revenue' },
                { key: 'clicks', label: 'Clicks' },
                { key: 'impressions', label: 'Impressions' },
                { key: 'reach', label: 'Reach' },
                { key: 'view', label: 'Video Views' },
                { key: 'trueplay', label: 'ThruPlay' },
                { key: 'conversions', label: 'Conversions' },
                { key: 'cpa', label: 'CPA' },
                { key: 'sessions', label: 'Sessions' },
                ...(hasAdjSessionsTrend ? [{ key: 'adjustSessions', label: 'Adj. Sessions' }] : []),
                ...(hasAdjPurchasesTrend ? [{ key: 'adjustPurchases', label: 'Adj. Purch.' }] : []),
                ...(hasAdjInstallsTrend ? [{ key: 'adjustInstalls', label: 'Adj. Installs' }] : []),
                { key: 'cpc', label: 'CPC' },
                { key: 'cps', label: 'CPS' },
                { key: 'roas', label: 'ROAS' }
              ];
              return (
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 overflow-x-auto">
                  {metricOptions.map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setSelectedTrendMetric(opt.key)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors whitespace-nowrap ${
                    selectedTrendMetric === opt.key ? 'bg-blue-600 text-white rounded-md shadow-sm' : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
              );
            })()}
          </div>
        </div>
        <div className="p-4 space-y-6">
      {monthlyPivot.months.length === 0 ? (
        <div className="p-8 text-center text-gray-500">No monthly data</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider">Campaign Type</th>
                {monthlyPivot.months.map((mk) => (
                  <th key={mk} className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                    {new Date(mk + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                  </th>
                ))}
                <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {monthlyPivot.objectives.map((objective) => {
                const objKey = objective.id || objective.name;
                const isOpen = !!expandedObjectivesPivot[objKey];
                const objectiveTotalBucket = aggregateBuckets(objective.totals);
                return (
                  <React.Fragment key={objKey}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-900 font-medium">
                        <button
                          onClick={() => setExpandedObjectivesPivot((prev) => ({ ...prev, [objKey]: !prev[objKey] }))}
                          className="inline-flex items-center gap-2 text-left"
                        >
                          <span className={`inline-block transform transition-transform ${isOpen ? 'rotate-90' : ''} text-lg leading-none`}>▸</span>
                          {formatObjectiveName(objective.name || objective.id || '')}
                        </button>
                      </td>
                      {objective.totals.map((b) => (
                        <td key={b.monthKey} className="px-3 py-2 text-right text-gray-800">{formatPivotCell(selectedTrendMetric, b)}</td>
                      ))}
                      <td className="px-3 py-2 text-right text-gray-900 font-semibold">{formatPivotCell(selectedTrendMetric, objectiveTotalBucket)}</td>
                    </tr>
                    {isOpen && objective.campaigns.map((c) => {
                      const campTotalBucket = aggregateBuckets(c.totals);
                      return (
                        <tr key={c.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-700 pl-6">
                            <div className="flex items-center gap-2">
                              <span className="text-gray-400">•</span>
                              <span>{c.name}</span>
                            </div>
                          </td>
                          {c.totals.map((b) => (
                            <td key={b.monthKey} className="px-3 py-2 text-right text-gray-700">{formatPivotCell(selectedTrendMetric, b)}</td>
                          ))}
                          <td className="px-3 py-2 text-right text-gray-700">{formatPivotCell(selectedTrendMetric, campTotalBucket)}</td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 font-semibold text-gray-900">
                <td className="px-3 py-2 text-left">Total</td>
                {monthlyPivot.months.map((mk, idx) => {
                  const monthAgg = aggregateBuckets(monthlyPivot.objectives.map((o) => o.totals[idx]));
                  return (
                    <td key={mk} className="px-3 py-2 text-right">{formatPivotCell(selectedTrendMetric, monthAgg)}</td>
                  );
                })}
                <td className="px-3 py-2 text-right">
                  {formatPivotCell(
                    selectedTrendMetric,
                    aggregateBuckets(
                      monthlyPivot.objectives.flatMap((o) => o.totals)
                    )
                  )}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
        </div>
      </Card>

      {/* Last 10 Days Performance (Daily) - Pivot */}
      <Card className="rounded-2xl overflow-hidden shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Last 10 Days Performance</h2>
          <p className="text-sm text-gray-500 mt-1">Daily breakdown by campaign objective</p>
        </div>
        <div className="p-4">
          {dailyPivot10.days.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No daily data</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider">Campaign Type</th>
                    {dailyPivot10.days.map((dk) => (
                      <th key={dk} className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                        {new Date(dk).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </th>
                    ))}
                    <th className="px-3 py-2 text-right text-[11px] font-medium text-gray-500 uppercase tracking-wider">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {dailyPivot10.objectives.map((objective) => {
                    const objKey = objective.id || objective.name;
                    const isOpen = !!expandedObjectivesPivot[`daily_${objKey}`];
                    const objectiveTotalBucket = aggregateBuckets(objective.totals);
                    return (
                      <React.Fragment key={objKey}>
                        <tr className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-900 font-medium">
                            <button
                              onClick={() => setExpandedObjectivesPivot((prev) => ({ ...prev, [`daily_${objKey}`]: !prev[`daily_${objKey}`] }))}
                              className="inline-flex items-center gap-2 text-left"
                            >
                              <span className={`inline-block transform transition-transform ${isOpen ? 'rotate-90' : ''} text-lg leading-none`}>▸</span>
                              {formatObjectiveName(objective.name || objective.id || '')}
                            </button>
                          </td>
                          {objective.totals.map((b) => (
                            <td key={b.dayKey} className="px-3 py-2 text-right text-gray-800">{formatPivotCell(selectedTrendMetric, b)}</td>
                          ))}
                          <td className="px-3 py-2 text-right text-gray-900 font-semibold">{formatPivotCell(selectedTrendMetric, objectiveTotalBucket)}</td>
                        </tr>
                        {isOpen && objective.campaigns.map((c) => {
                          const campTotalBucket = aggregateBuckets(c.totals);
                          return (
                            <tr key={c.id} className="hover:bg-gray-50">
                              <td className="px-3 py-2 text-gray-700 pl-6">
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-400">•</span>
                                  <span>{c.name}</span>
                                </div>
                              </td>
                              {c.totals.map((b) => (
                                <td key={b.dayKey} className="px-3 py-2 text-right text-gray-700">{formatPivotCell(selectedTrendMetric, b)}</td>
                              ))}
                              <td className="px-3 py-2 text-right text-gray-700">{formatPivotCell(selectedTrendMetric, campTotalBucket)}</td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 font-semibold text-gray-900">
                    <td className="px-3 py-2 text-left">Total</td>
                    {dailyPivot10.days.map((dk, idx) => {
                      const dayAgg = aggregateBuckets(dailyPivot10.objectives.map((o) => o.totals[idx]));
                      return (
                        <td key={dk} className="px-3 py-2 text-right">{formatPivotCell(selectedTrendMetric, dayAgg)}</td>
                      );
                    })}
                    <td className="px-3 py-2 text-right">
                      {formatPivotCell(
                        selectedTrendMetric,
                        aggregateBuckets(
                          dailyPivot10.objectives.flatMap((o) => o.totals)
                        )
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </Card>


      {/* Change History Slide-Over Panel */}
      {showChangeHistory && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setShowChangeHistory(false)} />
          <div className="relative w-full max-w-lg bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center">
                  <History className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">Change History</h2>
                  <p className="text-xs text-gray-500">{changeHistoryTotal} total changes</p>
                </div>
              </div>
              <button onClick={() => setShowChangeHistory(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="px-6 py-3 border-b border-gray-50 flex gap-2">
              {[
                { key: 'all', label: 'All' },
                { key: 'status_change', label: 'Status' },
                { key: 'budget_change', label: 'Budget' }
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setChangeHistoryFilter(f.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    changeHistoryFilter === f.key
                      ? 'bg-indigo-100 text-indigo-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
              {changeHistoryLoading && changeHistoryData.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
                </div>
              ) : changeHistoryData.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500">No changes recorded yet</p>
                  <p className="text-xs text-gray-400 mt-1">Changes to campaigns will appear here</p>
                </div>
              ) : (
                <>
                  {changeHistoryData.map((item, idx) => {
                    const isStatus = item.action === 'status_change';
                    const isBudget = item.action === 'budget_change';
                    const timeAgo = (() => {
                      const diff = Date.now() - new Date(item.created_at).getTime();
                      const mins = Math.floor(diff / 60000);
                      if (mins < 1) return 'Just now';
                      if (mins < 60) return `${mins}m ago`;
                      const hours = Math.floor(mins / 60);
                      if (hours < 24) return `${hours}h ago`;
                      const days = Math.floor(hours / 24);
                      if (days < 7) return `${days}d ago`;
                      return new Date(item.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
                    })();
                    const formatValue = (val, field) => {
                      if (!val) return '—';
                      if (field === 'amount_micros' || field === 'daily_budget') {
                        const num = parseFloat(val) / 1000000;
                        return `₺${num.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
                      }
                      return val;
                    };
                    return (
                      <div key={item.id || idx} className={`p-3.5 rounded-xl border transition-colors ${item.success ? 'border-gray-100 bg-white hover:bg-gray-50/50' : 'border-red-100 bg-red-50/30'}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 min-w-0 flex-1">
                            <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center mt-0.5 ${
                              !item.success ? 'bg-red-100' : isStatus ? 'bg-amber-100' : isBudget ? 'bg-blue-100' : 'bg-gray-100'
                            }`}>
                              {!item.success ? <XCircle className="w-4 h-4 text-red-500" /> :
                               isStatus ? <Activity className="w-4 h-4 text-amber-600" /> :
                               <DollarSign className="w-4 h-4 text-blue-600" />}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider ${
                                  isStatus ? 'bg-amber-100 text-amber-700' : isBudget ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {isStatus ? 'Status' : isBudget ? 'Budget' : item.action}
                                </span>
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                  item.platform === 'GOOGLE_ADS' ? 'bg-emerald-50 text-emerald-600' : 'bg-blue-50 text-blue-600'
                                }`}>
                                  {item.platform === 'GOOGLE_ADS' ? 'Google' : 'Meta'}
                                </span>
                              </div>
                              {item.entity_name && (
                                <p className="text-sm font-medium text-gray-800 mt-1 truncate">{item.entity_name}</p>
                              )}
                              <div className="flex items-center gap-1.5 mt-1 text-xs">
                                <span className="text-gray-400">{formatValue(item.old_value, item.field_name)}</span>
                                <span className="text-gray-300">→</span>
                                <span className={`font-medium ${item.success ? 'text-gray-700' : 'text-red-600'}`}>
                                  {formatValue(item.new_value, item.field_name)}
                                </span>
                              </div>
                              {!item.success && item.error_message && (
                                <p className="text-xs text-red-500 mt-1 truncate">{item.error_message}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex-shrink-0 text-right">
                            <div className="flex items-center gap-1 text-[11px] text-gray-400">
                              <Clock className="w-3 h-3" />
                              {timeAgo}
                            </div>
                            {item.user_email && (
                              <p className="text-[10px] text-gray-400 mt-0.5 truncate max-w-[120px]">{item.user_name || item.user_email}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {changeHistoryData.length < changeHistoryTotal && (
                    <button
                      onClick={() => fetchChangeHistory(changeHistoryData.length, true)}
                      disabled={changeHistoryLoading}
                      className="w-full py-2.5 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {changeHistoryLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      Load More ({changeHistoryTotal - changeHistoryData.length} remaining)
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}